import { isCvImportReviewCancelled, isCvImportSourceChanged } from '../../lib/cv-import-review';
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Platform } from "react-native";
import { useGlobalSearchParams, usePathname, useRouter } from "expo-router";
import { useAuth } from "../../hooks/useAuth";
import { useCVData } from "../../lib/cv-mock-data";
import { resolveImportedCvName } from "../../lib/imported-cv-name";
import type { ExportFormat } from "../../lib/cv-export";
import {
  buildCvProfileReplacementFromParsed,
  buildSaveSectionsFromCvProfile,
  buildSaveSectionsFromParsedCvData,
  assessParsedCvForReplacement,
  ImportedCvIncompleteError,
} from "../../lib/cv-save-sections";
import { clearUaePassPostAuthTarget } from "../../lib/uae-pass-session";
import {
  enrichCv,
  getOnboardingProfile,
  OnboardingApiError,
  saveOnboardingPreferences,
} from "../../lib/onboarding-api";
import { saveCvConfig } from "../../lib/profile-api";
import {
  DEFAULT_CV_STYLE_SEGMENT_ID,
  getSegmentById,
} from "./cv-style-segments";
import {
  DEFAULT_CV_PRESET_LAYOUT,
  getCvPresetById,
  isCvPresetId,
  type CvPreset,
} from "./cv-presets";
import { isSkillStyleId } from "./cv-preset-rows";
import {
  getClaimedCvFlowSteps,
  insertManualCvSections,
  resolveCvHealthNextStep,
  resolveCvHealthPreviousStep,
  reconcileWebStepHistory,
  recordWebStepNavigation,
  canReturnByHistoryBack,
  resolveNextOnboardingStep,
  resolvePreviousOnboardingStep,
  shouldSkipCvHealthForImport,
  type CvEntryMode,
} from "./onboarding-navigation";
import {
  clearPostAuthTarget,
  getPostAuthDestinationHref,
  normalizePostAuthTarget,
  postAuthTargetSurvivesOnboarding,
  readPostAuthTarget,
  readPostAuthTargetAsync,
} from "../../lib/auth-handoff";
import {
  abandonPendingResumeImport,
  readResumeHandoffTokenAsync,
} from "../../lib/resume-handoff";
import {
  useSetIntent,
  useSaveSections,
  useCompleteOnboarding,
  useOnboardingState,
  useOnboardingProfile,
  useOnboardingCompany,
} from "../../hooks/useOnboardingApi";
import {
  useSaveJobPreferences,
  useSaveProfilePrivacy,
} from "../../hooks/useProfileApi";
import {
  getAuthStateSnapshot,
  mergeAuthenticatedUser,
  refreshAuthenticatedUser,
} from "../../lib/app-auth";
import {
  captureProductException,
  getErrorObservabilityProperties,
  trackProductEvent,
} from "../../lib/product-analytics";
import {
  readWebStorage,
  removeWebStorage,
  writeWebStorage,
} from "../../lib/web-storage";
import {
  DEFAULT_SEEKER_ONBOARDING_INTENT,
  SEEKER_ONLY_LAUNCH,
} from "../../lib/launch-scope";
import type {
  CvHealthReceipt,
  CvParseQuality,
  OnboardingIntent as OnboardingIntentType,
  OnboardingState,
  ParsedCvData,
  ProfileContactVisibilitySettings,
  ProfilePhotoVisibility,
  ProfilePrivacySettings,
  ProfileVisibility as SharedProfileVisibility,
} from "@tabbio/types";
import {
  DEFAULT_PROFILE_PRIVACY_SETTINGS,
  deriveCvVisibilityDefaults,
  normalizeProfilePrivacySettings,
} from "@tabbio/utils/profile-privacy";
import { useToast } from "../../hooks/useToast";

function readNationality(value: unknown) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return "";
  }

  const nationality = (value as Record<string, unknown>).nationality;
  return typeof nationality === "string" ? nationality : "";
}

function readUsername(value: unknown) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return "";
  }

  const username = (value as Record<string, unknown>).username;
  return typeof username === "string" ? username.trim() : "";
}

function hasCompletedAuthSnapshot() {
  const authState = getAuthStateSnapshot();
  return (
    authState.status === "authenticated" &&
    authState.user?.isOnboardingCompleted === true
  );
}

// ─── Types ──────────────────────────────────────────────────────

export type OnboardingIntent = "job" | "freelancer" | "company" | null;
export type ProfileVisibility = SharedProfileVisibility;
export type PostOnboardingRoute =
  | "/(app)/cv"
  | "/(app)/chat"
  | "/(app)/jobs"
  | "/(employer)/chat"
  | "/(employer)/jobs/create"
  | "/(employer)/candidates";

/**
 * Where a seeker lands when onboarding completes: their own CV, the thing
 * they just built, named and saw (peak-end). Never chat: the welcome upsell
 * and the product tour left the completion path.
 */
const SEEKER_COMPLETION_ROUTE: PostOnboardingRoute = "/(app)/cv";

interface ProfileSetupData {
  name: string;
  username: string;
  nationality: string;
  phone: string;
  avatarUrl: string;
}

interface CompanySetupData {
  name: string;
  slug: string;
  industry: string;
  employeeCount: string;
  location: string;
  website: string;
  description: string;
  logo: string;
}

// ─── Background CV enrichment (post AI input) ──────────────────
//
// Upload only extracts; the AI writing pass starts right after the import is
// persisted (the server resolves the tier from the user's real subscription:
// paid → Gemini Pro with premium prompts) and the selected Gulf CV style
// becomes a generation input. It runs in the background while the user fills
// the basics screens; results hydrate into the draft section-by-section,
// skipping any section whose step the user already reached.

export type CvEnrichmentStatus = "idle" | "running" | "done" | "failed";

export interface StartCvEnrichmentOptions {
  styleSegmentId?: string;
  /**
   * A paid plan was just chosen or confirmed (choose-ai): run for the
   * premium model, at most once per uploaded CV, so a paid user passing the
   * chooser on every Back and forward never restarts a minutes-long rewrite.
   * With the writing pass waiting for "Fix my CV", this is normally the
   * FIRST run of the import (ensureCvEnrichmentStarted then has nothing to
   * add); it only supersedes a free run when one is already in flight.
   */
  paidUpgrade?: boolean;
}

export interface CvEnrichmentState {
  status: CvEnrichmentStatus;
  /** Whether the completed run used the premium model. */
  premium: boolean;
  /** The style segment the current/last run generated with. */
  styleSegmentId: string | null;
}

const BASE_STEPS = ["intent-select"] as const;
const COMPANY_STEPS = ["company-setup", "company-next-action"] as const;
// The imported-CV flow (docs/planning/onboarding-conversion-flow.md, owner
// decision 2026-10-04): the honest check, ONE soft plan chooser behind "Fix
// my CV", the fix, then the user's own CV.
//
//   ai-input -> cv-health -> [choose-ai, only behind Fix] -> this-is-you
//     -> complete (the CV preview) -> who-sees-cv -> /cv
//
// "cv-health" checks an imported CV (upload, pasted text, resume-handoff
// auto-import) right after it is saved. Its "Fix my CV" is the single
// monetization touch of onboarding: it opens the SOFT choose-ai picker (a
// Free path is always one visible tap away, nothing blocks on payment),
// shown once, and every way out of it returns to the report, which then
// writes and fixes the CV. Skip leaves the CV exactly as uploaded and never
// shows the picker. Only the import handler (persistImportedCvAndGoToHeader)
// routes to cv-health. The manual build jumps from ai-input straight to
// this-is-you, and the claimed-CV flow has its own step list, so neither
// ever sees the check or the picker. It is never a gate: a disabled feature
// or a failed review advances (around the picker).
//
// The signup path is deliberately short: identity, then the CV itself.
// Everything the AI already extracted (contacts, experience, education,
// skills, languages, summary) is reviewed IN the preview by tapping a
// section, not by walking a wizard, because users abandoned before ever
// seeing their CV. There is no paywall and no survey at the end: who-sees-cv
// is the last step and its Continue completes onboarding (the terminal hard
// paywall and the where-heard survey stay PRESERVED steps; the survey is
// asked once in the app instead, when a CV is opened from My Tabbio).
const CV_STEPS = ["ai-input", "cv-health", "choose-ai", "this-is-you"] as const;
// The "basics" screens: they own an isolated segmented bar on native
// (OnboardingFormHeader's `activeSegment`), so the shared phase bar is
// suppressed for them there. how-to-reach-you is only linear on the manual
// build (see MANUAL_CV_WIZARD_STEPS); ai-input, cv-health and choose-ai have
// no bar.
const CV_BASICS_STEPS: readonly string[] = ["this-is-you", "how-to-reach-you"];
const COMPLETE_STEP = "complete" as const;
// Visibility + public-indexing consent. Runs AFTER the preview: a user decides
// who may see the CV once they have seen it, and this step still hard-blocks on
// the consent checkbox, so it must stay in the linear flow (the Prisma default
// is `visibility: public`, and completing onboarding publishes the profile).
const PRIVACY_STEP = "who-sees-cv" as const;
// Editors: not part of the linear flow for an imported CV. They are reachable
// by tapping a section in the CV preview (the header opens how-to-reach-you)
// or a row in the "Complete my CV" checklist, and their screens return to
// `complete` on save/back. The exception is the manual build path, where
// MANUAL_CV_WIZARD_STEPS splices the contact screen and the non-optional
// sections into the flow until the preview is reached (see
// insertManualCvSections).
const OPTIONAL_STEPS = [
  "how-to-reach-you",
  "experience",
  "education",
  "languages",
  "skills",
  "summary",
  "certifications",
  "highlights",
] as const;
// Preserved for post-onboarding placement. These are intentionally omitted from
// the linear flow so signup onboarding skips them while routes/components stay
// valid. "paywall" is the terminal hard paywall: it no longer ends onboarding
// (the soft choose-ai picker behind "Fix my CV" is the single monetization
// step), but its route and screen remain valid for deep links and upgrade
// surfaces. "where-heard" is the attribution survey (owner, 2026-10-04: "the
// survey after CV completion is a bad idea; it should be inside, after he
// clicks the CV inside My Tabbio"): the same question is asked once in the
// app by components/cv/WhereHeardPrompt; the route stays valid for old deep
// links and finishWhereHeard still completes onboarding from it.
const PRESERVED_STEPS = [
  "personal-info",
  "job-preferences",
  "paywall",
  "where-heard",
] as const;
// Limited launch (SEEKER_ONLY_LAUNCH): no screens are forced before AI input
// anymore: ai-input is the first step in CV_STEPS, so this is empty.
const SEEKER_LAUNCH_FORCED_STEPS = [] as const;
const SEEKER_LAUNCH_DISABLED_PATHS = new Set([
  "/intent",
  "/company-setup",
  "/company-next-action",
]);

// The terminal run after the preview: privacy alone. Its Continue completes
// onboarding (see the completion seam in nextStep). There is no paywall and no
// survey here: the where-heard question moved into the app (WhereHeardPrompt).
const TERMINAL_STEPS: readonly OnboardingStep[] = [PRIVACY_STEP];

// Steps at/after the CV preview: reaching any of them ends the manual
// section wizard (the sections become off-flow preview editors again).
const CV_PREVIEW_OR_LATER_STEPS: readonly string[] = [
  COMPLETE_STEP,
  ...TERMINAL_STEPS,
];

const ALL_STEPS = [
  ...BASE_STEPS,
  ...COMPANY_STEPS,
  ...CV_STEPS,
  ...OPTIONAL_STEPS,
  ...PRESERVED_STEPS,
  PRIVACY_STEP,
  COMPLETE_STEP,
] as const;
export type OnboardingStep = (typeof ALL_STEPS)[number];

type NavigationMode = "push" | "replace";

interface ResumeProgressSnapshot {
  hasProfile: boolean;
  hasCompany: boolean;
  hasExperience: boolean;
  hasEducation: boolean;
  hasCertifications: boolean;
  hasSkills: boolean;
}

// Step name → web route path mapping
const STEP_ROUTES: Record<OnboardingStep, string> = {
  "intent-select": "/(onboarding)/intent",
  "company-setup": "/(onboarding)/company-setup",
  "company-next-action": "/(onboarding)/company-next-action",
  "ai-input": "/(onboarding)/ai-input",
  "cv-health": "/(onboarding)/cv-health",
  "choose-ai": "/(onboarding)/choose-ai",
  "this-is-you": "/(onboarding)/this-is-you",
  "how-to-reach-you": "/(onboarding)/how-to-reach-you",
  "who-sees-cv": "/(onboarding)/who-sees-cv",
  education: "/(onboarding)/education",
  experience: "/(onboarding)/experience",
  skills: "/(onboarding)/skills",
  languages: "/(onboarding)/languages",
  summary: "/(onboarding)/summary",
  "job-preferences": "/(onboarding)/job-preferences",
  "personal-info": "/(onboarding)/personal-info",
  certifications: "/(onboarding)/certifications",
  highlights: "/(onboarding)/highlights",
  complete: "/(onboarding)/complete",
  "where-heard": "/(onboarding)/where-heard",
  paywall: "/(onboarding)/paywall",
};

// Normalized web onboarding paths.
const ROUTE_TO_STEP: Record<string, OnboardingStep> = {
  "/intent": "intent-select",
  "/company-setup": "company-setup",
  "/company-next-action": "company-next-action",
  "/ai-input": "ai-input",
  "/cv-health": "cv-health",
  "/choose-ai": "choose-ai",
  "/this-is-you": "this-is-you",
  "/how-to-reach-you": "how-to-reach-you",
  "/who-sees-cv": "who-sees-cv",
  "/education": "education",
  "/experience": "experience",
  "/skills": "skills",
  "/languages": "languages",
  "/summary": "summary",
  "/job-preferences": "job-preferences",
  "/personal-info": "personal-info",
  "/certifications": "certifications",
  "/highlights": "highlights",
  "/complete": "complete",
  "/where-heard": "where-heard",
  "/paywall": "paywall",
};

const isWeb = Platform.OS === "web";
const COMPLETION_SESSION_RETRY_MS = 250;
const COMPLETION_SESSION_MAX_ATTEMPTS = 12;

function normalizeIntent(intent?: string | null): OnboardingIntent {
  if (!intent) return null;
  if (intent === "brand") return "freelancer";
  if (intent === "hire") return "company";
  if (intent === "job" || intent === "freelancer" || intent === "company")
    return intent;
  return null;
}

function getFlowSteps(
  intent: OnboardingIntent,
  options?: {
    /**
     * Manual CV build ("Build my CV manually"): the user has no imported
     * content to preview, so the section-entry wizard joins the linear flow
     * between how-to-reach-you and the preview. Once the preview has been
     * reached the sections revert to off-flow editors (see
     * insertManualCvSections in onboarding-navigation.ts).
     */
    manualCvSections?: boolean;
    /**
     * A CV handoff claimed during onboarding (intent 'update'): the server
     * already hydrated the profile, so the flow is the short claim variant
     * (ai-input -> complete -> this-is-you -> who-sees-cv). Takes precedence
     * over manualCvSections; who-sees-cv is terminal (see nextStep).
     */
    claimedCvFlow?: boolean;
  },
): OnboardingStep[] {
  if (options?.claimedCvFlow) {
    return getClaimedCvFlowSteps<OnboardingStep>();
  }

  const finalize = (flow: OnboardingStep[]) =>
    options?.manualCvSections ? insertManualCvSections(flow) : flow;

  if (SEEKER_ONLY_LAUNCH) {
    // ai-input → cv-health (imports only) → choose-ai (soft, only behind
    // "Fix my CV"; Skip goes around it) → this-is-you → [manual: contacts +
    // sections] → complete (the CV preview) → who-sees-cv → the CV. No
    // terminal paywall, no survey. The manual build enters at this-is-you,
    // so it passes neither cv-health nor choose-ai (see
    // resolvePreviousOnboardingStep).
    return finalize([...CV_STEPS, COMPLETE_STEP, ...TERMINAL_STEPS]);
  }

  if (!intent) return finalize([...BASE_STEPS, COMPLETE_STEP]);
  if (intent === "company")
    return finalize([...BASE_STEPS, ...COMPANY_STEPS, COMPLETE_STEP]);
  return finalize([...BASE_STEPS, ...CV_STEPS, COMPLETE_STEP]);
}

function getDefaultDestination(intent: OnboardingIntent): PostOnboardingRoute {
  if (SEEKER_ONLY_LAUNCH) {
    return intent === "freelancer" ? "/(app)/jobs" : SEEKER_COMPLETION_ROUTE;
  }

  if (intent === "company") return "/(employer)/chat";
  if (intent === "freelancer") return "/(app)/jobs";
  return SEEKER_COMPLETION_ROUTE;
}

function createOnboardingAnalyticsSessionId() {
  return `onb_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function getOnboardingFlowName(intent: OnboardingIntent) {
  if (intent === "company") {
    return "company";
  }

  if (intent === "job" || intent === "freelancer") {
    return "cv";
  }

  return "pre_intent";
}

function getOnboardingStepAnalyticsProperties(
  step: OnboardingStep,
  intent: OnboardingIntent,
  extra?: Record<string, unknown>,
) {
  const flow = getFlowSteps(intent);
  const index = flow.indexOf(step);

  return {
    funnel: "onboarding",
    platform: isWeb ? "web" : "native",
    onboardingFlow: getOnboardingFlowName(intent),
    intent: intent ?? "undecided",
    step,
    stepIndex: index >= 0 ? index + 1 : null,
    stepTotal: flow.length,
    isCvStep: CV_STEPS.includes(step as (typeof CV_STEPS)[number]),
    ...extra,
  };
}

function getStepDirection(
  fromStep: OnboardingStep,
  toStep: OnboardingStep,
  intent: OnboardingIntent,
) {
  const flow = getFlowSteps(intent);
  const fromIndex = flow.indexOf(fromStep);
  const toIndex = flow.indexOf(toStep);
  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
    return "unknown";
  }

  return toIndex > fromIndex ? "forward" : "back";
}

function getResumeProgressAnalytics(snapshot?: OnboardingState | null) {
  if (!snapshot) {
    return {
      hasSavedProgress: false,
      hasProfile: false,
      hasCompany: false,
      hasExperience: false,
      hasEducation: false,
      hasCertifications: false,
      hasSkills: false,
    };
  }

  return {
    hasSavedProgress: Boolean(
      snapshot.hasProfile ||
      snapshot.hasCompany ||
      snapshot.hasExperience ||
      snapshot.hasEducation ||
      snapshot.hasCertifications ||
      snapshot.hasSkills ||
      snapshot.intent,
    ),
    hasProfile: Boolean(snapshot.hasProfile),
    hasCompany: Boolean(snapshot.hasCompany),
    hasExperience: Boolean(snapshot.hasExperience),
    hasEducation: Boolean(snapshot.hasEducation),
    hasCertifications: Boolean(snapshot.hasCertifications),
    hasSkills: Boolean(snapshot.hasSkills),
  };
}

function getProfileFormAnalytics(data: ProfileSetupData) {
  return {
    hasName: Boolean(data.name.trim()),
    hasUsername: Boolean(data.username.trim()),
    hasNationality: Boolean(data.nationality.trim()),
    hasPhone: Boolean(data.phone.trim()),
    hasAvatar: Boolean(data.avatarUrl.trim()),
  };
}

function getCompanyFormAnalytics(data: CompanySetupData) {
  return {
    hasCompanyName: Boolean(data.name.trim()),
    hasCompanySlug: Boolean(data.slug.trim()),
    hasIndustry: Boolean(data.industry.trim()),
    hasEmployeeCount: Boolean(data.employeeCount.trim()),
    hasLocation: Boolean(data.location.trim()),
    hasWebsite: Boolean(data.website.trim()),
    hasDescription: Boolean(data.description.trim()),
    hasLogo: Boolean(data.logo.trim()),
  };
}

function getCvProfileAnalytics(profile: Record<string, any>) {
  const skillCount = Array.isArray(profile.skillGroups)
    ? profile.skillGroups.reduce(
        (total: number, group: { items?: unknown[] }) =>
          total + (Array.isArray(group.items) ? group.items.length : 0),
        0,
      )
    : Array.isArray(profile.skills)
      ? profile.skills.length
      : 0;

  return {
    hasHeadline: Boolean(profile.headline || profile.title),
    hasOverview: Boolean(profile.overview || profile.summary || profile.about),
    experienceCount: Array.isArray(profile.experience)
      ? profile.experience.length
      : 0,
    educationCount: Array.isArray(profile.education)
      ? profile.education.length
      : 0,
    certificationCount: Array.isArray(profile.certifications)
      ? profile.certifications.length
      : 0,
    highlightCount: Array.isArray(profile.highlights)
      ? profile.highlights.length
      : 0,
    skillCount,
    hasJobPreferences: Boolean(profile.jobPreferences),
  };
}

function getSaveSectionsAnalytics(sections: Record<string, unknown>) {
  const recordCount = (key: string) => {
    const value = sections[key];
    return Array.isArray(value) ? value.length : 0;
  };

  return {
    savedSectionKeys: Object.keys(sections).sort(),
    experienceCount: recordCount("experience"),
    educationCount: recordCount("education"),
    certificationCount: recordCount("certifications"),
    highlightCount: recordCount("highlights"),
    projectCount: recordCount("projects"),
    hasProfilePatch: Boolean(sections.profile),
    hasLanguages: Array.isArray(sections.languages),
    replaceExisting:
      (sections as { replaceExisting?: unknown }).replaceExisting === true,
  };
}

function getStepCompletionAnalytics(
  step: OnboardingStep,
  profileData: ProfileSetupData,
  companyData: CompanySetupData,
  cvProfile: Record<string, any>,
) {
  if (step === "this-is-you") {
    return getProfileFormAnalytics(profileData);
  }

  if (step === "company-setup" || step === "company-next-action") {
    return getCompanyFormAnalytics(companyData);
  }

  if (
    CV_STEPS.includes(step as (typeof CV_STEPS)[number]) ||
    OPTIONAL_STEPS.includes(step as (typeof OPTIONAL_STEPS)[number]) ||
    step === PRIVACY_STEP ||
    step === "complete"
  ) {
    return getCvProfileAnalytics(cvProfile);
  }

  return {};
}

function stepIndex(step: OnboardingStep) {
  return ALL_STEPS.indexOf(step);
}

function getResumeStep(
  snapshot: ResumeProgressSnapshot,
  intent: OnboardingIntent,
): OnboardingStep {
  if (SEEKER_ONLY_LAUNCH) {
    // The basics screens (this-is-you / how-to-reach-you) aren't separately
    // server-persisted, so the only honest signal is whether CV sections exist.
    // Any saved CV content means the import already landed, and the section
    // editors are no longer linear steps to resume into, so resume at the
    // preview: the user sees their CV and edits from there.
    if (
      snapshot.hasSkills ||
      snapshot.hasEducation ||
      snapshot.hasExperience
    ) {
      return COMPLETE_STEP;
    }
    return "ai-input";
  }

  if (!snapshot.hasProfile) return "intent-select";
  if (!intent) return "intent-select";

  if (intent === "company") {
    return snapshot.hasCompany ? "company-next-action" : "company-setup";
  }

  if (snapshot.hasSkills) return "summary";
  if (snapshot.hasEducation) return "languages";
  if (snapshot.hasExperience) return "education";
  return "ai-input";
}

function normalizeOnboardingPath(path?: string | null): string {
  if (!path) return "/ai-input";

  let normalized = path.split("?")[0]?.split("#")[0] ?? "";
  if (normalized.startsWith("/(onboarding)")) {
    normalized = normalized.replace("/(onboarding)", "");
  }
  normalized = normalized.replace(/\/+$/, "");
  if (!normalized || normalized === "/") return "/ai-input";
  return normalized.startsWith("/") ? normalized : `/${normalized}`;
}

function getStepFromPath(path?: string | null): OnboardingStep | null {
  const normalized = normalizeOnboardingPath(path);
  if (SEEKER_ONLY_LAUNCH && SEEKER_LAUNCH_DISABLED_PATHS.has(normalized)) {
    return null;
  }
  return ROUTE_TO_STEP[normalized] ?? null;
}

function getWebProgressStorageKey(userId?: string) {
  return `tabbio:onboarding:web-max-step:${userId ?? "anonymous"}`;
}

function getCvEntryModeStorageKey(userId?: string) {
  return `tabbio:onboarding:cv-entry-mode:${userId ?? "anonymous"}`;
}

function readStoredCvEntryMode(key: string): CvEntryMode | null {
  if (!isWeb || typeof window === "undefined") return null;
  const stored = readWebStorage(key, "session");
  return stored === "manual" || stored === "imported" ? stored : null;
}

function writeStoredCvEntryMode(key: string, mode: CvEntryMode) {
  if (!isWeb || typeof window === "undefined") return;
  writeWebStorage(key, mode, "session");
}

// Claim-flow flag persistence: same session-storage pattern as cvEntryMode so
// the short claim flow survives a web reload mid-onboarding. Native keeps the
// ref alone (the provider stays mounted for the whole flow, like cvEntryMode).
function getCvClaimFlowStorageKey(userId?: string) {
  return `tabbio:onboarding:cv-claim-flow:${userId ?? "anonymous"}`;
}

function readStoredCvClaimFlow(key: string): boolean {
  if (!isWeb || typeof window === "undefined") return false;
  return readWebStorage(key, "session") === "1";
}

function writeStoredCvClaimFlow(key: string, active: boolean) {
  if (!isWeb || typeof window === "undefined") return;
  if (active) {
    writeWebStorage(key, "1", "session");
  } else {
    removeWebStorage(key, "session");
  }
}

// CV Health step state (the check between ai-input and this-is-you). Same
// session-storage pattern as cvEntryMode, so a web reload or browser Back to
// /cv-health adopts the saved report instead of replaying the check. Native
// keeps it in provider state alone (the wizard never unmounts the provider).
function getCvHealthStorageKey(userId?: string) {
  return `tabbio:onboarding:cv-health:${userId ?? "anonymous"}`;
}

const CV_HEALTH_IMPORT_SOURCES: readonly CvHealthImportSource[] = [
  "file",
  "text",
  "handoff",
];

interface CvHealthStepState {
  /** The storage key this state belongs to (web), so a user switch never writes stale state. */
  storageKey: string;
  import: CvHealthImportContext | null;
  phase: CvHealthStepPhase;
  /** The honest check of the upload as parsed: its report, and its score. */
  reportId: string | null;
  importScore: number | null;
  /** The finished fix (phase `fixed`): the report of the fixed CV and the receipt Undo sends back. */
  fix: CvHealthFixRecord | null;
  /** "Fix my CV" was tapped; the fix runs once the plan chooser is done. */
  pendingFix: boolean;
  /** The plan chooser completed on the way to the fix; it never shows twice. */
  chooserDone: boolean;
  /**
   * CV Health was off for this import, so the step skipped itself: Back goes
   * around it (it would only skip forward again, a dead Back).
   */
  skipped: boolean;
}

function createCvHealthStepState(storageKey: string): CvHealthStepState {
  return {
    storageKey,
    import: null,
    phase: "checking",
    reportId: null,
    importScore: null,
    fix: null,
    pendingFix: false,
    chooserDone: false,
    skipped: false,
  };
}

const CV_HEALTH_STEP_PHASES: readonly CvHealthStepPhase[] = [
  "checking",
  "report",
  "fixing",
  "fixed",
];

function readStoredCvHealthFix(value: unknown): CvHealthFixRecord | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (typeof record.reportId !== "string" || !record.reportId) return null;
  const receipt = record.receipt as Record<string, unknown> | null | undefined;
  if (receipt === null || receipt === undefined) {
    return { reportId: record.reportId, receipt: null };
  }
  // Shape check only: the server validates the receipt it is sent back.
  if (
    typeof receipt !== "object" ||
    typeof receipt.id !== "string" ||
    typeof receipt.fromReportId !== "string" ||
    !Array.isArray(receipt.items) ||
    !Array.isArray(receipt.skipped)
  ) {
    return null;
  }
  return { reportId: record.reportId, receipt: receipt as unknown as CvHealthReceipt };
}

function readStoredCvParseQuality(value: unknown): CvParseQuality | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  // Shape check only: the server validates importSignals with Zod.
  return typeof record.status === "string" &&
    typeof record.completeness === "string" &&
    typeof record.fallbackUsed === "boolean"
    ? (value as CvParseQuality)
    : null;
}

function readStoredCvHealthState(key: string): CvHealthStepState {
  const empty = createCvHealthStepState(key);
  if (!isWeb || typeof window === "undefined") return empty;
  const raw = readWebStorage(key, "session");
  if (!raw) return empty;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed !== "object") return empty;
    const source = CV_HEALTH_IMPORT_SOURCES.includes(
      parsed.source as CvHealthImportSource,
    )
      ? (parsed.source as CvHealthImportSource)
      : null;
    const importedAt =
      typeof parsed.importedAt === "number" ? parsed.importedAt : null;
    return {
      storageKey: key,
      import:
        importedAt === null
          ? null
          : {
              quality: readStoredCvParseQuality(parsed.quality),
              source,
              importedAt,
            },
      phase: CV_HEALTH_STEP_PHASES.includes(parsed.phase as CvHealthStepPhase)
        ? (parsed.phase as CvHealthStepPhase)
        : "checking",
      reportId:
        typeof parsed.reportId === "string" && parsed.reportId
          ? parsed.reportId
          : null,
      importScore:
        typeof parsed.importScore === "number" && Number.isFinite(parsed.importScore)
          ? parsed.importScore
          : null,
      fix: readStoredCvHealthFix(parsed.fix),
      pendingFix: parsed.pendingFix === true,
      chooserDone: parsed.chooserDone === true,
      skipped: parsed.skipped === true,
    };
  } catch {
    removeWebStorage(key, "session");
    return empty;
  }
}

function writeStoredCvHealthState(state: CvHealthStepState) {
  if (!isWeb || typeof window === "undefined") return;
  if (
    !state.import &&
    !state.reportId &&
    !state.fix &&
    state.phase === "checking" &&
    !state.pendingFix &&
    !state.chooserDone &&
    !state.skipped
  ) {
    removeWebStorage(state.storageKey, "session");
    return;
  }
  writeWebStorage(
    state.storageKey,
    JSON.stringify({
      quality: state.import?.quality ?? null,
      source: state.import?.source ?? null,
      importedAt: state.import?.importedAt ?? null,
      reportId: state.reportId,
      importScore: state.importScore,
      // The receipt rides along (same session store as the CV draft itself),
      // so Undo still works after a reload of the fixed report.
      fix: state.fix,
      phase: state.phase,
      pendingFix: state.pendingFix,
      chooserDone: state.chooserDone,
      skipped: state.skipped,
    }),
    "session",
  );
}

// nextStep() takes an optional completion action for the step analytics.
// Only these values are honoured: nextStep is also passed straight to
// onPress/onSave props, which hand it an event or form data, and those must
// read as a plain "next".
const NEXT_STEP_COMPLETION_ACTIONS: ReadonlySet<string> = new Set<
  NonNullable<NextStepOptions["completionAction"]>
>(["continue", "skip", "skip_disabled", "continue_without_checking", "fix_with_ai"]);

function readNextStepOptions(value: unknown): NextStepOptions {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const record = value as Record<string, unknown>;
  const completionAction =
    typeof record.completionAction === "string" &&
    NEXT_STEP_COMPLETION_ACTIONS.has(record.completionAction)
      ? (record.completionAction as NextStepOptions["completionAction"])
      : undefined;
  const historyMode =
    record.historyMode === "push" || record.historyMode === "replace"
      ? record.historyMode
      : undefined;
  const fromStep =
    typeof record.fromStep === "string" &&
    (ALL_STEPS as readonly string[]).includes(record.fromStep)
      ? (record.fromStep as OnboardingStep)
      : undefined;
  return { completionAction, historyMode, fromStep };
}

function getCvDraftStorageKey(userId?: string) {
  return `tabbio:onboarding:cv-draft:${userId ?? "anonymous"}`;
}

const CV_DRAFT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
// Bump when the StoredCvDraft shape (or downstream CVProfile shape that
// gets persisted inside `profile`) changes. Drafts from older versions are
// silently discarded on read: server data is still the canonical source.
const CV_DRAFT_SCHEMA_VERSION = 1;

type StoredCvDraft = {
  version: number;
  profile: unknown;
  savedAt: number;
};

type ReadDraftResult = {
  profile: unknown | null;
  error?: unknown;
};

type WriteDraftResult =
  | { ok: true }
  | { ok: false; isQuota: boolean; error: unknown };

function readStoredCvDraft(key: string): ReadDraftResult {
  if (!isWeb || typeof window === "undefined") return { profile: null };
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(key);
  } catch (error) {
    return { profile: null, error };
  }
  if (!raw) return { profile: null };
  try {
    const parsed = JSON.parse(raw) as StoredCvDraft;
    if (!parsed || typeof parsed !== "object") {
      return { profile: null };
    }
    if (parsed.version !== CV_DRAFT_SCHEMA_VERSION) {
      // Stale schema: discard. Don't feed pre-migration data into the
      // current CVProfile shape (the cast at the call site bypasses Zod).
      try {
        window.localStorage.removeItem(key);
      } catch {
        // best-effort cleanup
      }
      return { profile: null };
    }
    if (
      typeof parsed.savedAt === "number" &&
      Date.now() - parsed.savedAt > CV_DRAFT_MAX_AGE_MS
    ) {
      try {
        window.localStorage.removeItem(key);
      } catch {
        // best-effort cleanup
      }
      return { profile: null };
    }
    return { profile: parsed.profile ?? null };
  } catch (error) {
    // Corrupted JSON: drop the poisoned entry so we don't keep
    // tripping over it every mount, and bubble the error up so callers
    // can captureProductException once per session.
    try {
      window.localStorage.removeItem(key);
    } catch {
      // best-effort cleanup
    }
    return { profile: null, error };
  }
}

function hasCvSectionContent(profile: unknown): boolean {
  if (!profile || typeof profile !== "object") return false;
  const record = profile as Record<string, any>;
  const hasItems = (value: unknown) => Array.isArray(value) && value.length > 0;

  if (
    hasItems(record.experience) ||
    hasItems(record.education) ||
    hasItems(record.certifications) ||
    hasItems(record.highlights) ||
    hasItems(record.languages)
  ) {
    return true;
  }

  if (
    Array.isArray(record.skillGroups) &&
    record.skillGroups.some(
      (group: { items?: unknown }) =>
        Array.isArray(group.items) && group.items.length > 0,
    )
  ) {
    return true;
  }

  if (
    (typeof record.headline === "string" && record.headline.trim()) ||
    (typeof record.summary === "string" && record.summary.trim())
  ) {
    return true;
  }

  const prefs = record.jobPreferences;
  if (prefs && typeof prefs === "object") {
    return Boolean(
      hasItems(prefs.jobTitles) ||
      hasItems(prefs.locationTypes) ||
      hasItems(prefs.preferredLocations) ||
      hasItems(prefs.employmentTypes) ||
      hasItems(prefs.searchAreas),
    );
  }

  return false;
}

function writeStoredCvDraft(key: string, profile: unknown): WriteDraftResult {
  if (!isWeb || typeof window === "undefined") return { ok: true };
  try {
    window.localStorage.setItem(
      key,
      JSON.stringify({
        version: CV_DRAFT_SCHEMA_VERSION,
        profile,
        savedAt: Date.now(),
      } satisfies StoredCvDraft),
    );
    return { ok: true };
  } catch (error) {
    const isQuota =
      error instanceof DOMException &&
      (error.name === "QuotaExceededError" ||
        (error as { code?: number }).code === 22);
    return { ok: false, isQuota, error };
  }
}

function clearStoredCvDraft(key: string) {
  if (!isWeb || typeof window === "undefined") return;
  removeWebStorage(key, "local");
}

function readStoredWebMaxStep(key: string): OnboardingStep | null {
  if (!isWeb || typeof window === "undefined") return null;
  try {
    const raw = readWebStorage(key, "session");
    if (!raw) return null;
    if (Object.prototype.hasOwnProperty.call(STEP_ROUTES, raw)) {
      return raw as OnboardingStep;
    }
    return null;
  } catch {
    return null;
  }
}

function writeStoredWebMaxStep(key: string, step: OnboardingStep) {
  if (!isWeb || typeof window === "undefined") return;
  try {
    writeWebStorage(key, step, "session");
  } catch {
    // Best effort only.
  }
}

/** How the CV that reaches the CV Health step was imported. */
export type CvHealthImportSource = "file" | "text" | "handoff";

/** What the import handler hands the CV Health step. */
export interface CvHealthImportContext {
  /** Privacy-safe parse quality, forwarded as the review's `importSignals`. */
  quality: CvParseQuality | null;
  source: CvHealthImportSource | null;
  importedAt: number;
  /**
   * The public CV Health report the handoff came from. The review sends it,
   * and the API adopts that report when the CV reads the same, so the score
   * the visitor saw before signing up is the score onboarding shows (LM1).
   */
  cvHealthToken?: string | null;
}

/**
 * `checking` runs the honest check of the upload; `report` shows its settled
 * report (never replays the check); `fixing` is the fix round (the return
 * from the plan chooser with a pending "Fix my CV", restarted from its first
 * stage after a reload); `fixed` shows the report of the fixed CV with Undo.
 */
/**
 * The in-memory CV keys a CV Health apply or undo can change on the server
 * (summary, headline, experience bullets and descriptions, skills, profile
 * links), plus highlights, which ride with the summary block.
 */
const CV_HEALTH_WRITABLE_PROFILE_KEYS = [
  "headline",
  "summary",
  "highlights",
  "skillGroups",
  "experience",
  "website",
  "linkedin",
  "github",
  "dribbble",
  "behance",
] as const;

export type CvHealthStepPhase = "checking" | "report" | "fixing" | "fixed";

/** A finished onboarding fix: what a remount shows, and what Undo sends back. */
export interface CvHealthFixRecord {
  /** The report of the fixed CV (the recheck the apply returned). */
  reportId: string;
  /** Null when nothing needed writing (no Undo, no "changes saved"). */
  receipt: CvHealthReceipt | null;
}

export interface NextStepOptions {
  /**
   * `completionAction` of the step's `onboarding_step_completed` event
   * (default "next"): how the CV Health report was left. `fix_with_ai` is
   * "Fix my CV", the one exit that leads into the plan chooser; `skip` leaves
   * the CV exactly as uploaded.
   */
  completionAction?:
    | "continue"
    | "skip"
    | "skip_disabled"
    | "continue_without_checking"
    | "fix_with_ai";
  /** Web history mode (default push). A skipped step replaces, so browser Back never bounces through it. */
  historyMode?: NavigationMode;
  /**
   * The step of the screen calling, for a caller that may hold a nextStep
   * from an earlier render (choose-ai's one-time mount effect). On web the
   * call goes ahead only while the address bar still shows that step.
   */
  fromStep?: OnboardingStep;
}

/**
 * `nextStep()` stays a zero-argument call for every existing caller (and is
 * still safe to hand to onPress/onSave); the CV Health step passes options.
 */
export interface NextStepFn {
  (): Promise<void>;
  (options: NextStepOptions): Promise<void>;
}

interface OnboardingContextValue {
  currentStep: number;
  stepName: OnboardingStep;
  totalSteps: number;
  cvStepCount: number;
  cvStepIndex: number;
  progressTotal: number;
  progressIndex: number;
  /** Progress scoped to the current phase (forced basics vs CV form steps). */
  phaseTotal: number;
  phaseIndex: number;

  profileData: ProfileSetupData;
  setProfileData: (data: Partial<ProfileSetupData>) => void;
  /**
   * True once profile setup (the "This Is You" step) has been completed this
   * session or in a resumed one. Lets the step restore the user's already-claimed
   * Tabbio handle on back-navigation instead of re-deriving it from the name
   * (issue #167).
   */
  hasCompletedProfileSetup: boolean;

  profileVisibility: ProfileVisibility;
  setProfileVisibility: (visibility: ProfileVisibility) => void;
  publicIndexingAcknowledged: boolean;
  setPublicIndexingAcknowledged: (acknowledged: boolean) => void;
  photoVisibility: ProfilePhotoVisibility;
  setPhotoVisibility: (visibility: ProfilePhotoVisibility) => void;
  contactVisibility: ProfileContactVisibilitySettings;
  setContactVisibility: React.Dispatch<
    React.SetStateAction<ProfileContactVisibilitySettings>
  >;

  companyData: CompanySetupData;
  setCompanyData: (data: Partial<CompanySetupData>) => void;

  intent: OnboardingIntent;
  setIntent: (intent: OnboardingIntent) => void;
  selectIntentAndAdvance: (intent: OnboardingIntent) => void;
  isIntentTransitioning: boolean;
  postOnboardingRoute: PostOnboardingRoute;
  setPostOnboardingRoute: (route: PostOnboardingRoute) => void;

  nextStep: NextStepFn;
  goToCvHeader: () => void;
  startManualCvBuild: () => void;
  /**
   * Enter the short claimed-CV flow after a successful in-onboarding handoff
   * claim (intent 'update'): jumps to the CV preview; who-sees-cv completes.
   */
  enterClaimedCvFlow: () => void;
  /**
   * Whether the short claimed-CV flow is active. A getter because the flag
   * lives on a ref (set before the preview mounts), so a render-time read is
   * reliable without adding reactive state.
   */
  isClaimedCvFlow: () => boolean;
  persistImportedCvAndGoToHeader: (
    parsed: ParsedCvData,
    options?: {
      mode?: NavigationMode;
      presetStyleId?: string;
      /** Parse quality of this import: gates the CV Health unreadable state. */
      quality?: CvParseQuality | null;
      importEvidenceId?: string;
      source?: CvHealthImportSource;
      /** The public CV Health report the handoff came from, if any. */
      cvHealthToken?: string | null;
    },
  ) => Promise<void>;
  goToStep: (step: OnboardingStep) => Promise<void>;
  prevStep: () => void;
  /** Advance past the current optional step without saving its form. */
  skipStep: () => void;
  skipToComplete: () => void;
  completeOnboarding: () => void;
  /** Finish the PRESERVED where-heard survey (old deep links only); it
   *  completes onboarding directly, as it did when it was the last step. */
  finishWhereHeard: (action: "next" | "skip") => Promise<void>;

  /** CV-preview sub-view of the complete step, lifted to the provider so it
   *  survives the complete → who-sees-cv → back round-trip (issue #50). */
  cvPreviewOpen: boolean;
  setCvPreviewOpen: (open: boolean) => void;

  /** Paid export format the user asked for before hitting the paywall, lifted
   *  to the provider so it survives the complete → /upgrade → returnTo round-trip
   *  and the export can auto-resume once entitlement is granted (issue #289). */
  pendingDownloadFormat: ExportFormat | null;
  setPendingDownloadFormat: (format: ExportFormat | null) => void;

  /**
   * CV Health step (imports only, between ai-input and the plan chooser). Set
   * by the import handler; null when the step was reached without an import
   * in this session (a web reload restores it from session storage).
   */
  cvHealthImport: CvHealthImportContext | null;
  cvHealthPhase: CvHealthStepPhase;
  setCvHealthPhase: (phase: CvHealthStepPhase) => void;
  /**
   * The settled honest report of this import (the upload as parsed) and its
   * score. Once known, a remount of the step (Back from the chooser or from
   * this-is-you, browser Back, reload) fetches and adopts it instead of
   * replaying the check; the score is where the fixed gauge starts from.
   */
  cvHealthReportId: string | null;
  cvHealthImportScore: number | null;
  setCvHealthImportReport: (report: { id: string; score: number | null } | null) => void;
  /**
   * The finished fix (phase `fixed`): a remount fetches its report and keeps
   * Undo. `null` clears it (Undo, a new import) and returns to `report`.
   */
  cvHealthFix: CvHealthFixRecord | null;
  setCvHealthFix: (fix: CvHealthFixRecord | null) => void;
  /**
   * After CV Health applied or undid changes on the server: reload the CV
   * sections those writes can touch into the in-memory CV, so a later step
   * that saves sections (a preview editor) never writes the old text back.
   */
  refreshCvSectionsFromServer: () => Promise<void>;
  /**
   * "Fix my CV" was tapped on the report: the plan chooser shows, and every
   * way out of it returns to cv-health (phase `fixing`) to run the fix (see
   * resolveCvHealthNextStep). Cleared when the fix is done, on Skip, and
   * when the user leaves the step.
   */
  cvHealthPendingFix: boolean;
  setCvHealthPendingFix: (pending: boolean) => void;
  /**
   * The plan chooser already completed on the way to the fix. From then on
   * "Fix my CV" (after an Undo) runs right on the step: it never shows twice.
   */
  cvHealthChooserDone: boolean;
  /**
   * The chooser has nothing to offer (already on Plus or Pro) and the fix
   * ran on the step: it counts as passed.
   */
  markCvHealthChooserDone: () => void;

  /**
   * Background tier+style-aware CV enrichment (the writing pass). For an
   * import that CV Health will check it is NOT kicked at import: the report
   * must describe the upload the user made, and the writing waits for "Fix
   * my CV" (after the chooser, at the chosen tier) so stage 1 "Writing your
   * CV…" genuinely covers it. It is kicked at import only when the check
   * will skip itself (a typed description with nothing to check), and by the
   * step on a feature-off skip or "Continue without checking". Skip never
   * writes: the CV stays exactly as uploaded, so the editor's report matches
   * the one the user saw.
   */
  cvEnrichment: CvEnrichmentState;
  /** Persisted career-stage style, independent of enrichment job state. */
  selectedCvStyleSegmentId: string | null;
  /** (Re)run enrichment: a style pick re-runs with the new style. */
  startCvEnrichment: (options?: StartCvEnrichmentOptions) => void;
  /**
   * Kick the writing pass for the current import unless a run already
   * belongs to it (the chooser's paid re-run, or an earlier kick): one run
   * per import. Returns whether a run is in flight afterwards, so the fix
   * round knows to wait for it.
   */
  ensureCvEnrichmentStarted: () => boolean;
  /** Suggested Gulf CV style for the current draft (picker pre-selection). */
  suggestedStyleSegmentId: string;

  /**
   * CV preset or catalog skill chosen on the AI-input screen, or null. Both
   * are `styleSegmentId` values (`preset-*` or `skill:<skillId>`) riding the
   * enrichment pipeline; `cvPresetLayout` is the look the pick implies.
   */
  cvPresetId: string | null;
  cvPresetLayout: CvPresetLayout;
  /** `layout` carries the pick's visual config (skills declare their own). */
  applyCvPreset: (presetId: string, layout?: CvPresetLayout) => Promise<void>;
  clearCvPreset: () => void;
  isApplyingCvPreset: boolean;

  isCompleting: boolean;
  onboardingError: string | null;
  clearError: () => void;
  isLoadingState: boolean;
}

export type CvPresetLayout = CvPreset['layout'];

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

// ─── Provider ───────────────────────────────────────────────────

export function OnboardingProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const params = useGlobalSearchParams<{ entry?: string; next?: string }>();
  const { user, isOnboardingCompleted } = useAuth();
  const { profile: cvProfile, profileRef, updateProfile, cvConfig } = useCVData();
  const toast = useToast();

  const [currentStep, setCurrentStep] = useState(
    stepIndex(SEEKER_ONLY_LAUNCH ? "ai-input" : "intent-select"),
  );
  const [intent, setIntentState] = useState<OnboardingIntent>(null);
  const [postOnboardingRoute, setPostOnboardingRouteState] =
    useState<PostOnboardingRoute>(getDefaultDestination(null));
  const [isIntentTransitioning, setIsIntentTransitioning] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  // CV-preview sub-view of the complete step (issue #50): lifted here so it
  // persists across the complete → who-sees-cv → back navigation.
  // Opens on the CV itself, not the checklist: seeing the finished CV as early
  // as possible is the whole reason `complete` moved up the flow. The checklist
  // is still one Back away.
  const [cvPreviewOpen, setCvPreviewOpen] = useState(true);
  // Paid export format requested before the paywall (issue #289): lifted here so
  // it persists across the /upgrade round-trip and the export can auto-resume.
  const [pendingDownloadFormat, setPendingDownloadFormat] =
    useState<ExportFormat | null>(null);
  const [cvEnrichment, setCvEnrichment] = useState<CvEnrichmentState>({
    status: "idle",
    premium: false,
    styleSegmentId: null,
  });
  const [selectedCvStyleSegmentId, setSelectedCvStyleSegmentId] = useState<
    string | null
  >(null);
  const pendingEnrichmentStyleIdRef = useRef<string | null>(null);
  // The look the applied pick declares. Hardcoded presets could derive this
  // from CV_PRESETS, but a catalog skill's layout comes with the pick (and is
  // persisted with it), so the applied value is stored explicitly.
  const [appliedPresetLayout, setAppliedPresetLayout] =
    useState<CvPresetLayout | null>(null);
  const [isApplyingCvPreset, setIsApplyingCvPreset] = useState(false);
  // Monotonic run id: a re-run (style change) supersedes an in-flight run so
  // the stale response can't clobber the newer one.
  const cvEnrichmentRunRef = useRef(0);
  // Bumped by every CV import. A run belongs to the import it started for:
  // an older import's run is never "the same run" as a newer one's, and its
  // result is dropped (runId) so it can't overwrite the newer upload.
  const cvImportGenerationRef = useRef(0);
  const cvEnrichmentRunImportRef = useRef(0);
  const paidEnrichmentImportRef = useRef<number | null>(null);
  const [onboardingError, setOnboardingError] = useState<string | null>(null);
  const [profileVisibility, setProfileVisibilityState] =
    useState<ProfileVisibility>(
      DEFAULT_PROFILE_PRIVACY_SETTINGS.profileVisibility,
    );
  const [publicIndexingAcknowledged, setPublicIndexingAcknowledged] =
    useState(false);
  const [photoVisibility, setPhotoVisibilityState] =
    useState<ProfilePhotoVisibility>(
      DEFAULT_PROFILE_PRIVACY_SETTINGS.photoVisibility,
    );
  const [contactVisibility, setContactVisibilityState] =
    useState<ProfileContactVisibilitySettings>(
      DEFAULT_PROFILE_PRIVACY_SETTINGS.contactVisibility,
    );
  const [cvVisibilityDefaults, setCvVisibilityDefaults] = useState(
    DEFAULT_PROFILE_PRIVACY_SETTINGS.cvVisibilityDefaults,
  );
  const [profileData, setProfileDataState] = useState<ProfileSetupData>({
    // UAE PASS `User.name` is verified legal identity, not the editable CV
    // display name. Let the saved Profile (or provider first/last fallback)
    // seed this field instead of leaking the legal name into the CV.
    name: user?.isUaePassVerified ? "" : (user?.name ?? ""),
    username: (user as any)?.username ?? "",
    nationality: "",
    phone: "",
    avatarUrl: "",
  });
  const [companyData, setCompanyDataState] = useState<CompanySetupData>({
    name: "",
    slug: "",
    industry: "",
    employeeCount: "",
    location: "",
    website: "",
    description: "",
    logo: "",
  });
  const [webMaxReachedStep, setWebMaxReachedStep] =
    useState<OnboardingStep>("ai-input");
  const [hasLocallyCompletedProfileSetup, setHasLocallyCompletedProfileSetup] =
    useState(false);

  const hasHydratedSavedProfileRef = useRef(false);
  const hasHydratedSavedPrivacyRef = useRef(false);
  const hasHydratedSavedCompanyRef = useRef(false);
  const hasAppliedNativeRestoreRef = useRef(false);
  const hasInitializedWebProgressRef = useRef(false);
  const webMaxReachedStepRef = useRef<OnboardingStep>("ai-input");
  const intentTransitionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const isOnboardingCompletedRef = useRef(isOnboardingCompleted);
  const routePostAuthTarget = normalizePostAuthTarget(params.next);
  const storedPostAuthTarget = readPostAuthTarget();
  // Any post-auth onboarding entry (profile-setup / intent links, fresh signup).
  const hasOnboardingEntryRequest =
    params.entry === "profile-setup" ||
    params.entry === "intent" ||
    routePostAuthTarget === "onboarding-intent" ||
    storedPostAuthTarget === "onboarding-intent" ||
    routePostAuthTarget === "onboarding-profile-setup" ||
    storedPostAuthTarget === "onboarding-profile-setup";
  const requestedOnboardingEntryStep: OnboardingStep | null = SEEKER_ONLY_LAUNCH
    ? // Limited launch: every onboarding entry lands at the flow start
      // (ai-input: the first step in CV_STEPS).
      hasOnboardingEntryRequest
      ? "ai-input"
      : null
    : // Legacy non-seeker flow (dead under SEEKER_ONLY_LAUNCH): all entry links
      // resolve to the first base step, intent-select.
      params.entry === "profile-setup" ||
        params.entry === "intent" ||
        routePostAuthTarget === "onboarding-intent" ||
        storedPostAuthTarget === "onboarding-intent" ||
        routePostAuthTarget === "onboarding-profile-setup" ||
        storedPostAuthTarget === "onboarding-profile-setup"
      ? "intent-select"
      : null;
  const requestedNativeEntryStep = !isWeb ? requestedOnboardingEntryStep : null;
  const forcedNativeEntryStepRef = useRef<OnboardingStep | null>(
    !isWeb ? requestedNativeEntryStep : null,
  );
  const stickyNativeEntryStepRef = useRef<OnboardingStep | null>(
    forcedNativeEntryStepRef.current,
  );
  const onboardingAnalyticsSessionIdRef = useRef(
    createOnboardingAnalyticsSessionId(),
  );
  const hasTrackedOnboardingStartRef = useRef(false);
  const hasTrackedOnboardingResumeRef = useRef(false);
  const completedOnboardingAnalyticsRef = useRef(false);
  const currentStepAnalyticsRef = useRef<{
    step: OnboardingStep;
    intent: OnboardingIntent;
    viewedAt: number;
  } | null>(null);
  const onboardingAnalyticsStateRef = useRef<{
    step: OnboardingStep;
    intent: OnboardingIntent;
    isLoadingState: boolean;
  }>({
    step: "ai-input",
    intent: null,
    isLoadingState: true,
  });

  // API mutations
  const setIntentMutation = useSetIntent();
  const saveSectionsMutation = useSaveSections();
  const saveJobPreferencesMutation = useSaveJobPreferences();
  const saveProfilePrivacyMutation = useSaveProfilePrivacy();
  const queryClient = useQueryClient();
  const completeMutation = useCompleteOnboarding();

  // Load saved onboarding state for resume flow
  const { data: savedState, isLoading: isLoadingState } = useOnboardingState();

  useEffect(() => {
    const storedStyleSegmentId = savedState?.styleSegmentId;
    // A resumed session may carry a legacy career-stage segment, a preset
    // picked on the AI-input screen, or a catalog skill (`skill:<skillId>`);
    // all three are valid style ids.
    if (
      !storedStyleSegmentId ||
      (!getSegmentById(storedStyleSegmentId) &&
        !isCvPresetId(storedStyleSegmentId) &&
        !isSkillStyleId(storedStyleSegmentId))
    ) {
      return;
    }
    setSelectedCvStyleSegmentId((current) => current ?? storedStyleSegmentId);
    // A skill pick's look was persisted alongside it (the catalog may not be
    // loaded on resume); restore it so the preview styles correctly.
    const storedLayout = savedState?.presetLayout;
    if (storedLayout) {
      setAppliedPresetLayout((current) => current ?? storedLayout);
    }
  }, [savedState?.styleSegmentId, savedState?.presetLayout]);

  // Load full profile data if state says we have saved data
  const shouldLoadProfile =
    !!savedState && !savedState.isCompleted && savedState.hasProfile;
  const { data: savedProfile, isLoading: isLoadingProfile } =
    useOnboardingProfile(shouldLoadProfile);

  const profilePrivacy = useMemo<ProfilePrivacySettings>(() => {
    const derivedDefaults = deriveCvVisibilityDefaults({
      photoVisibility,
      contactVisibility,
    });

    return normalizeProfilePrivacySettings({
      profileVisibility,
      photoVisibility,
      contactVisibility,
      cvVisibilityDefaults: {
        ...cvVisibilityDefaults,
        showEmail: derivedDefaults.showEmail,
        showPhone: derivedDefaults.showPhone,
        showPhoto: derivedDefaults.showPhoto,
      },
    });
  }, [
    contactVisibility,
    cvVisibilityDefaults,
    photoVisibility,
    profileVisibility,
  ]);

  // Load company data for company onboarding resume
  const shouldLoadCompany =
    !SEEKER_ONLY_LAUNCH &&
    !!savedState &&
    !savedState.isCompleted &&
    savedState.hasCompany &&
    normalizeIntent(savedState.intent) === "company";
  const { data: savedCompany, isLoading: isLoadingCompany } =
    useOnboardingCompany(shouldLoadCompany);

  const webProgressKey = useMemo(
    () => getWebProgressStorageKey(user?.id),
    [user?.id],
  );
  const cvDraftKey = useMemo(() => getCvDraftStorageKey(user?.id), [user?.id]);
  const cvEntryModeKey = useMemo(
    () => getCvEntryModeStorageKey(user?.id),
    [user?.id],
  );
  const cvEntryModeRef = useRef<CvEntryMode | null>(null);
  const hydratedCvEntryModeKeyRef = useRef<string | null>(null);
  const hasRestoredCvDraftRef = useRef(false);
  const hasReportedCvDraftReadFailureRef = useRef(false);
  const hasReportedCvDraftWriteFailureRef = useRef(false);
  const cvDraftWriteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const rawSavedIntent = normalizeIntent(savedState?.intent ?? null);
  const savedIntent = SEEKER_ONLY_LAUNCH
    ? rawSavedIntent === DEFAULT_SEEKER_ONBOARDING_INTENT
      ? rawSavedIntent
      : null
    : rawSavedIntent;
  const effectiveIntent = SEEKER_ONLY_LAUNCH
    ? DEFAULT_SEEKER_ONBOARDING_INTENT
    : (intent ?? savedIntent);

  const cvClaimFlowKey = useMemo(
    () => getCvClaimFlowStorageKey(user?.id),
    [user?.id],
  );
  const cvClaimFlowRef = useRef(false);
  const hydratedCvClaimFlowKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isWeb || hydratedCvEntryModeKeyRef.current === cvEntryModeKey) return;
    hydratedCvEntryModeKeyRef.current = cvEntryModeKey;
    cvEntryModeRef.current = readStoredCvEntryMode(cvEntryModeKey);
  }, [cvEntryModeKey]);

  useEffect(() => {
    if (!isWeb || hydratedCvClaimFlowKeyRef.current === cvClaimFlowKey) return;
    hydratedCvClaimFlowKeyRef.current = cvClaimFlowKey;
    cvClaimFlowRef.current =
      cvClaimFlowRef.current || readStoredCvClaimFlow(cvClaimFlowKey);
  }, [cvClaimFlowKey]);

  const setCvEntryMode = useCallback(
    (mode: CvEntryMode) => {
      cvEntryModeRef.current = mode;
      if (isWeb) {
        writeStoredCvEntryMode(cvEntryModeKey, mode);
      }
    },
    [cvEntryModeKey],
  );

  const setCvClaimFlow = useCallback(
    (active: boolean) => {
      cvClaimFlowRef.current = active;
      if (isWeb) {
        writeStoredCvClaimFlow(cvClaimFlowKey, active);
      }
    },
    [cvClaimFlowKey],
  );

  // CV Health step state. Read synchronously on web (a lazy initializer, not
  // an effect): the step's own mount effect runs before any provider effect,
  // and it must already see a saved report id to adopt it instead of starting
  // a second check.
  const cvHealthKey = useMemo(
    () => getCvHealthStorageKey(user?.id),
    [user?.id],
  );
  const [cvHealthState, setCvHealthState] = useState<CvHealthStepState>(() =>
    readStoredCvHealthState(getCvHealthStorageKey(user?.id)),
  );

  useEffect(() => {
    if (!isWeb || cvHealthState.storageKey === cvHealthKey) return;
    // The signed-in user changed (or resolved after the first render).
    setCvHealthState(readStoredCvHealthState(cvHealthKey));
  }, [cvHealthKey, cvHealthState.storageKey]);

  useEffect(() => {
    if (!isWeb || cvHealthState.storageKey !== cvHealthKey) return;
    writeStoredCvHealthState(cvHealthState);
  }, [cvHealthKey, cvHealthState]);

  const setCvHealthPhase = useCallback((phase: CvHealthStepPhase) => {
    setCvHealthState((current) =>
      current.phase === phase ? current : { ...current, phase },
    );
  }, []);

  const setCvHealthImportReport = useCallback(
    (report: { id: string; score: number | null } | null) => {
      const reportId = report?.id ?? null;
      const importScore = report?.score ?? null;
      setCvHealthState((current) =>
        current.reportId === reportId && current.importScore === importScore
          ? current
          : { ...current, reportId, importScore },
      );
    },
    [],
  );

  // A finished fix is shown (`fixed`); clearing it (Undo) returns to the
  // report. Either way the pending fix is spent.
  const setCvHealthFix = useCallback((fix: CvHealthFixRecord | null) => {
    setCvHealthState((current) => ({
      ...current,
      fix,
      phase: fix ? "fixed" : "report",
      pendingFix: false,
    }));
  }, []);

  const refreshCvSectionsFromServer = useCallback(async () => {
    const saved = await getOnboardingProfile();
    const patch: Record<string, unknown> = {};
    for (const key of CV_HEALTH_WRITABLE_PROFILE_KEYS) {
      if (key in saved) patch[key] = saved[key];
    }
    if (Object.keys(patch).length > 0) updateProfile(patch);
  }, [updateProfile]);

  const setCvHealthPendingFix = useCallback((pendingFix: boolean) => {
    setCvHealthState((current) =>
      current.pendingFix === pendingFix ? current : { ...current, pendingFix },
    );
  }, []);

  const markCvHealthChooserDone = useCallback(() => {
    setCvHealthState((current) =>
      current.chooserDone ? current : { ...current, chooserDone: true },
    );
  }, []);

  // nextStep/prevStep read the fix route through a ref (they are handed to
  // many callers; the flags change far less often than they are called).
  const cvHealthStateRef = useRef(cvHealthState);
  cvHealthStateRef.current = cvHealthState;

  // Manual-build section wizard gate. The section steps are part of the
  // linear flow ONLY until the user first reaches the CV preview; after that
  // they revert to off-flow editors opened from the preview (whose Save/Back
  // return there). Marked in setCurrentStepWithTrace so every navigation path
  // (native, web URL sync, resume) flips it exactly once.
  const hasReachedCvPreviewRef = useRef(false);
  const flowOptions = useCallback(
    () => ({
      manualCvSections:
        cvEntryModeRef.current === "manual" &&
        !hasReachedCvPreviewRef.current &&
        !(
          isWeb &&
          CV_PREVIEW_OR_LATER_STEPS.includes(webMaxReachedStepRef.current)
        ),
      claimedCvFlow: cvClaimFlowRef.current,
    }),
    [],
  );

  const routeStep = useMemo(
    () => (isWeb ? getStepFromPath(pathname) : null),
    [pathname],
  );
  const indexedStepName = (ALL_STEPS[currentStep] ??
    "ai-input") as OnboardingStep;
  const activeStepName = isWeb
    ? (routeStep ?? indexedStepName)
    : indexedStepName;
  const activeStepNameFromRender = activeStepName;
  /** The step in the address bar right now (web), else the rendered one. */
  const readLiveStepName = useCallback((fallback: OnboardingStep): OnboardingStep => {
    if (!isWeb || typeof window === "undefined") return fallback;
    return getStepFromPath(window.location.pathname) ?? fallback;
  }, []);
  const activeStep = stepIndex(activeStepName);

  const trackOnboardingEvent = useCallback(
    (
      event: string,
      options?: {
        step?: OnboardingStep;
        intent?: OnboardingIntent;
        properties?: Record<string, unknown>;
      },
    ) => {
      const step = options?.step ?? activeStepName;
      const intentForEvent =
        options && Object.prototype.hasOwnProperty.call(options, "intent")
          ? (options.intent ?? null)
          : effectiveIntent;

      trackProductEvent(event, {
        sessionId: onboardingAnalyticsSessionIdRef.current,
        ...getOnboardingStepAnalyticsProperties(
          step,
          intentForEvent,
          options?.properties,
        ),
      });
    },
    [activeStepName, effectiveIntent],
  );

  const setCurrentStepWithTrace = useCallback(
    (targetStep: OnboardingStep, _source: string) => {
      const nextIndex = stepIndex(targetStep);
      if (nextIndex < 0) {
        return;
      }

      if (CV_PREVIEW_OR_LATER_STEPS.includes(targetStep)) {
        hasReachedCvPreviewRef.current = true;
      }

      setCurrentStep((prev) => {
        if (prev === nextIndex) {
          return prev;
        }

        return nextIndex;
      });
    },
    [],
  );

  const mergedProgress = useMemo<ResumeProgressSnapshot>(() => {
    const saved = (savedState ?? {}) as Partial<OnboardingState>;
    const hasLocalExperience = (cvProfile.experience?.length ?? 0) > 0;
    const hasLocalEducation = (cvProfile.education?.length ?? 0) > 0;
    const hasLocalCertifications = (cvProfile.certifications?.length ?? 0) > 0;
    const hasLocalSkills =
      cvProfile.skillGroups?.some((g) => (g.items?.length ?? 0) > 0) ?? false;
    const hasLaterOnboardingProgress =
      !!saved.intent ||
      !!saved.hasCompany ||
      !!saved.hasExperience ||
      !!saved.hasEducation ||
      !!saved.hasCertifications ||
      !!saved.hasSkills;
    const hasPersistedProfileUsername =
      readUsername(savedProfile) ||
      (typeof user?.username === "string" ? user.username.trim() : "");
    const hasCompletedProfileSetup =
      !!saved.hasProfile &&
      (hasLaterOnboardingProgress || hasPersistedProfileUsername.length > 0);

    return {
      hasProfile: hasLocallyCompletedProfileSetup || hasCompletedProfileSetup,
      hasCompany: !!saved.hasCompany,
      hasExperience: !!saved.hasExperience || hasLocalExperience,
      hasEducation: !!saved.hasEducation || hasLocalEducation,
      hasCertifications: !!saved.hasCertifications || hasLocalCertifications,
      hasSkills: !!saved.hasSkills || hasLocalSkills,
    };
  }, [
    hasLocallyCompletedProfileSetup,
    savedProfile,
    savedState,
    cvProfile,
    user?.username,
  ]);

  const strictGateStep = useMemo(
    () =>
      requestedOnboardingEntryStep === "intent-select" && !SEEKER_ONLY_LAUNCH
        ? "intent-select"
        : getResumeStep(mergedProgress, effectiveIntent),
    [mergedProgress, effectiveIntent, requestedOnboardingEntryStep],
  );

  useEffect(() => {
    onboardingAnalyticsStateRef.current = {
      step: activeStepName,
      intent: effectiveIntent,
      isLoadingState,
    };
  }, [activeStepName, effectiveIntent, isLoadingState]);

  useEffect(() => {
    if (isLoadingState || hasTrackedOnboardingStartRef.current) {
      return;
    }

    hasTrackedOnboardingStartRef.current = true;
    const resumeProgress = getResumeProgressAnalytics(savedState ?? null);

    trackOnboardingEvent("onboarding_session_started", {
      properties: {
        requestedEntryStep: requestedOnboardingEntryStep,
        strictGateStep,
        resumed: resumeProgress.hasSavedProgress,
        ...resumeProgress,
      },
    });

    if (
      resumeProgress.hasSavedProgress &&
      !hasTrackedOnboardingResumeRef.current
    ) {
      hasTrackedOnboardingResumeRef.current = true;
      trackOnboardingEvent("onboarding_resumed", {
        properties: {
          requestedEntryStep: requestedOnboardingEntryStep,
          strictGateStep,
          ...resumeProgress,
        },
      });
    }
  }, [
    isLoadingState,
    requestedOnboardingEntryStep,
    savedState,
    strictGateStep,
    trackOnboardingEvent,
  ]);

  useEffect(() => {
    if (isLoadingState) {
      return;
    }

    const now = Date.now();
    const previous = currentStepAnalyticsRef.current;

    if (
      previous &&
      (previous.step !== activeStepName || previous.intent !== effectiveIntent)
    ) {
      trackOnboardingEvent("onboarding_step_left", {
        step: previous.step,
        intent: previous.intent,
        properties: {
          nextStep: activeStepName,
          nextIntent: effectiveIntent ?? "undecided",
          direction: getStepDirection(
            previous.step,
            activeStepName,
            effectiveIntent,
          ),
          durationMs: Math.max(now - previous.viewedAt, 0),
        },
      });
    }

    trackOnboardingEvent("onboarding_step_viewed", {
      properties: {
        ...getResumeProgressAnalytics(savedState ?? null),
      },
    });
    currentStepAnalyticsRef.current = {
      step: activeStepName,
      intent: effectiveIntent,
      viewedAt: now,
    };
  }, [
    activeStepName,
    effectiveIntent,
    isLoadingState,
    savedState,
    trackOnboardingEvent,
  ]);

  useEffect(() => {
    const sessionId = onboardingAnalyticsSessionIdRef.current;

    return () => {
      if (completedOnboardingAnalyticsRef.current) {
        return;
      }

      const state = onboardingAnalyticsStateRef.current;
      if (state.isLoadingState) {
        return;
      }

      const currentStepAnalytics = currentStepAnalyticsRef.current;
      trackProductEvent("onboarding_abandoned", {
        sessionId,
        ...getOnboardingStepAnalyticsProperties(state.step, state.intent, {
          durationMs: currentStepAnalytics
            ? Math.max(Date.now() - currentStepAnalytics.viewedAt, 0)
            : null,
        }),
      });
    };
  }, []);

  const updateWebMaxReachedStep = useCallback(
    (step: OnboardingStep, flowIntent: OnboardingIntent) => {
      if (!isWeb) return;
      const flow = getFlowSteps(flowIntent, flowOptions());
      if (!flow.includes(step)) return;

      setWebMaxReachedStep((prev) => {
        const prevInFlow = flow.includes(prev) ? prev : flow[0];
        const next =
          flow.indexOf(step) > flow.indexOf(prevInFlow) ? step : prevInFlow;
        webMaxReachedStepRef.current = next;
        writeStoredWebMaxStep(webProgressKey, next);
        return next;
      });
    },
    [flowOptions, webProgressKey],
  );

  // The history entries this wizard wrote (see reconcileWebStepHistory).
  const webStepHistoryRef = useRef<OnboardingStep[]>([]);
  // Set while a history.back() is in flight, so a second Back tap cannot
  // race the traversal with a replace of the entry being left.
  const historyBackPendingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!isWeb) return;
    if (historyBackPendingRef.current) {
      clearTimeout(historyBackPendingRef.current);
      historyBackPendingRef.current = null;
    }
    webStepHistoryRef.current = reconcileWebStepHistory(
      webStepHistoryRef.current,
      routeStep,
    );
    // A checkout return entry (`?checkout=success`) is never a step to go
    // back through: restoring it replays the return. Leaving it unrecorded
    // makes Back from the next step replace to a plain URL instead.
    if (
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).has("checkout")
    ) {
      webStepHistoryRef.current = [];
    }
  }, [routeStep]);
  useEffect(
    () => () => {
      if (historyBackPendingRef.current) clearTimeout(historyBackPendingRef.current);
    },
    [],
  );

  const goToStep = useCallback(
    (
      target: OnboardingStep,
      options?: {
        mode?: NavigationMode;
        updateWebProgress?: boolean;
        flowIntent?: OnboardingIntent;
      },
    ) => {
      const idx = stepIndex(target);
      if (idx < 0) return;
      setCurrentStepWithTrace(target, "goToStep");

      const mode = options?.mode ?? "replace";
      const flowIntent = options?.flowIntent ?? effectiveIntent;

      if (isWeb && options?.updateWebProgress !== false) {
        updateWebMaxReachedStep(target, flowIntent);
      }

      if (!isWeb) return;
      // The address bar, not this render's route: a goToStep held from an
      // earlier render (through a stale nextStep) would otherwise see the
      // step before it and skip a navigation the user still needs.
      const liveRouteStep =
        typeof window === "undefined"
          ? routeStep
          : getStepFromPath(window.location.pathname);
      if (liveRouteStep === target) return;

      const route = STEP_ROUTES[target];
      if (!route) return;
      if (mode === "push") {
        router.push(route as any);
      } else {
        router.replace(route as any);
      }
      webStepHistoryRef.current = recordWebStepNavigation(
        webStepHistoryRef.current,
        target,
        mode,
      );
    },
    [
      effectiveIntent,
      routeStep,
      router,
      setCurrentStepWithTrace,
      updateWebMaxReachedStep,
    ],
  );

  /**
   * Web: when the entry behind this one is `target`, step back through the
   * browser history (what browser Back would do) instead of writing a new
   * entry. False when it cannot; the caller navigates as before.
   */
  const returnByHistoryBack = useCallback(
    (target: OnboardingStep): boolean => {
      if (!isWeb || typeof window === "undefined") return false;
      // Already going back (a double tap): the traversal in flight wins.
      if (historyBackPendingRef.current) return true;
      if (!canReturnByHistoryBack(webStepHistoryRef.current, target)) {
        return false;
      }
      setCurrentStepWithTrace(target, "historyBack");
      webStepHistoryRef.current = webStepHistoryRef.current.slice(0, -1);
      // Cleared when the route changes; the timer is the fallback for a
      // traversal that never lands (e.g. blocked by the browser).
      historyBackPendingRef.current = setTimeout(() => {
        historyBackPendingRef.current = null;
      }, 1500);
      window.history.back();
      return true;
    },
    [setCurrentStepWithTrace],
  );

  const persistIntent = useCallback(
    (newIntent: OnboardingIntent) => {
      if (!newIntent) return;
      setIntentMutation.mutate(newIntent as OnboardingIntentType, {
        onSuccess: () => {
          trackOnboardingEvent("onboarding_intent_saved", {
            step: "intent-select",
            intent: newIntent,
            properties: {
              selectedIntent: newIntent,
              destination: getDefaultDestination(newIntent),
            },
          });
        },
        onError: (err) => {
          trackOnboardingEvent("onboarding_intent_save_failed", {
            step: "intent-select",
            intent: newIntent,
            properties: {
              selectedIntent: newIntent,
              ...getErrorObservabilityProperties(err),
            },
          });
          captureProductException(err, {
            feature: "onboarding",
            action: "set_intent",
            selectedIntent: newIntent,
          });
          toast.error(
            "Save failed",
            err instanceof Error ? err.message : "Could not save intent",
          );
        },
      });
    },
    [setIntentMutation, toast, trackOnboardingEvent],
  );

  // Restore intent + native progress from server.
  useEffect(() => {
    if (!savedState || savedState.isCompleted) return;

    if (savedIntent) {
      setIntentState((prev) => prev ?? savedIntent);
      // Only the unset default moves: a step that already chose a route
      // (the company branch's next action) keeps it.
      setPostOnboardingRouteState((prev) =>
        prev === getDefaultDestination(null)
          ? getDefaultDestination(savedIntent)
          : prev,
      );
    }

    // Restore the native step only once per mount. Otherwise a savedState
    // refetch (focus/reconnect) could re-run getResumeStep and yank the user
    // backward in the reordered flow.
    if (!isWeb && !hasAppliedNativeRestoreRef.current) {
      hasAppliedNativeRestoreRef.current = true;
      const stickyStep = stickyNativeEntryStepRef.current;
      if (stickyStep) {
        setCurrentStepWithTrace(stickyStep, "restore:sticky-step");
        if (forcedNativeEntryStepRef.current) {
          clearUaePassPostAuthTarget();
          forcedNativeEntryStepRef.current = null;
        }
        return;
      }

      const resumeStep = getResumeStep(
        {
          hasProfile: mergedProgress.hasProfile,
          hasCompany: !!savedState.hasCompany,
          hasExperience: !!savedState.hasExperience,
          hasEducation: !!savedState.hasEducation,
          hasCertifications: !!savedState.hasCertifications,
          hasSkills: !!savedState.hasSkills,
        },
        savedIntent,
      );
      setCurrentStepWithTrace(resumeStep, "restore:resume-step");
    }
  }, [
    mergedProgress.hasProfile,
    savedState,
    savedIntent,
    setCurrentStepWithTrace,
  ]);

  // Initialize web progress baseline once per mount.
  useEffect(() => {
    if (!isWeb) return;
    if (isLoadingState) return;
    if (hasInitializedWebProgressRef.current) return;
    hasInitializedWebProgressRef.current = true;

    const flow = getFlowSteps(effectiveIntent, flowOptions());
    const baseline = flow.includes(strictGateStep) ? strictGateStep : flow[0];
    const stored = readStoredWebMaxStep(webProgressKey);
    const initialStep =
      stored &&
      flow.includes(stored) &&
      flow.indexOf(stored) >= flow.indexOf(baseline)
        ? stored
        : baseline;

    setWebMaxReachedStep(initialStep);
    webMaxReachedStepRef.current = initialStep;
    writeStoredWebMaxStep(webProgressKey, initialStep);
  }, [isLoadingState, strictGateStep, effectiveIntent, webProgressKey, flowOptions]);

  // Strict deep-link gating + URL/state sync for web onboarding.
  useEffect(() => {
    if (!isWeb) return;
    if (isLoadingState) return;

    const requestedStep = routeStep ?? getStepFromPath(pathname);
    const flow = getFlowSteps(effectiveIntent, flowOptions());
    const baseline = flow.includes(strictGateStep) ? strictGateStep : flow[0];
    const refMaxStep = webMaxReachedStepRef.current;
    const maxByProgress = flow.includes(refMaxStep)
      ? refMaxStep
      : flow.includes(webMaxReachedStep)
        ? webMaxReachedStep
        : baseline;
    const allowedMaxPos = Math.max(
      flow.indexOf(baseline),
      flow.indexOf(maxByProgress),
    );
    const requestedPos = requestedStep ? flow.indexOf(requestedStep) : -1;

    // Section editors (experience, education, languages, skills, summary,
    // certifications, highlights) live outside the linear flow and are reached
    // by tapping a section in the CV preview or a checklist row. Once the user
    // has progressed to the complete step, allow them through the gate (sync the
    // step) instead of redirecting; they all return to the preview. Before
    // complete is reached, fall back to the deep-link redirect like any
    // out-of-range step.
    if (
      requestedStep &&
      (OPTIONAL_STEPS as readonly OnboardingStep[]).includes(requestedStep) &&
      // In the manual wizard the section steps ARE the flow; gate them like
      // any other linear step instead of preview-editor deep links.
      !flow.includes(requestedStep)
    ) {
      const completePos = flow.indexOf(COMPLETE_STEP);
      if (completePos >= 0 && allowedMaxPos >= completePos) {
        setCurrentStepWithTrace(requestedStep, "web:optional-step");
      } else {
        goToStep(flow[allowedMaxPos] ?? baseline, {
          mode: "replace",
          updateWebProgress: false,
          flowIntent: effectiveIntent,
        });
      }
      return;
    }

    if (!requestedStep || requestedPos < 0 || requestedPos > allowedMaxPos) {
      const redirectStep = flow[allowedMaxPos] ?? baseline;
      goToStep(redirectStep, {
        mode: "replace",
        updateWebProgress: false,
        flowIntent: effectiveIntent,
      });
      return;
    }

    const idx = stepIndex(requestedStep);
    if (idx >= 0) {
      setCurrentStepWithTrace(requestedStep, "web:route-sync");
    }
  }, [
    routeStep,
    pathname,
    isLoadingState,
    effectiveIntent,
    strictGateStep,
    webMaxReachedStep,
    goToStep,
    setCurrentStepWithTrace,
    flowOptions,
  ]);

  // Restore unsynced CV draft from localStorage on first mount. SCRUM-276
  // contract: edits made on a slow/unstable connection must survive a
  // refresh. If a local draft exists we trust it over `savedProfile` and
  // BLOCK the backend hydrate effect: otherwise an older backend snapshot
  // (v3) overwrites newer unsynced local edits (v4) and the user loses the
  // exact data this PR is supposed to protect. When there's no local
  // draft, leave hasHydratedSavedProfileRef untouched so the backend
  // hydrate useEffect runs normally.
  useEffect(() => {
    if (!isWeb) return;
    if (hasRestoredCvDraftRef.current) return;
    if (!user?.id) return;
    if (isLoadingState || isLoadingProfile) return;

    hasRestoredCvDraftRef.current = true;
    const { profile: draft, error } = readStoredCvDraft(cvDraftKey);

    if (error && !hasReportedCvDraftReadFailureRef.current) {
      hasReportedCvDraftReadFailureRef.current = true;
      captureProductException(error, {
        feature: "onboarding",
        action: "cv_draft_read_failed",
      });
    }

    if (draft && typeof draft === "object") {
      const savedProfileHasSections = hasCvSectionContent(savedProfile);
      const draftHasSections = hasCvSectionContent(draft);
      if (savedProfileHasSections && !draftHasSections) {
        clearStoredCvDraft(cvDraftKey);
        return;
      }
      hasHydratedSavedProfileRef.current = true;
      updateProfile(draft as any);
    }
  }, [
    cvDraftKey,
    isLoadingProfile,
    isLoadingState,
    savedProfile,
    updateProfile,
    user?.id,
  ]);

  // Mirror CVDataProvider state to localStorage so a slow/unstable connection
  // (SCRUM-276) doesn't wipe in-progress work. Debounced 500ms so rapid
  // field edits don't thrash JSON.stringify. If the write fails (Safari
  // private mode, quota exceeded), capture once and toast on quota: the
  // entire safety net is defeated silently otherwise.
  useEffect(() => {
    if (!isWeb) return;
    if (!user?.id) return;
    if (isOnboardingCompletedRef.current) return;
    if (shouldLoadProfile && !hasHydratedSavedProfileRef.current) return;
    if (cvDraftWriteTimerRef.current) {
      clearTimeout(cvDraftWriteTimerRef.current);
    }
    cvDraftWriteTimerRef.current = setTimeout(() => {
      const result = writeStoredCvDraft(cvDraftKey, cvProfile);
      cvDraftWriteTimerRef.current = null;
      if (!result.ok && !hasReportedCvDraftWriteFailureRef.current) {
        hasReportedCvDraftWriteFailureRef.current = true;
        captureProductException(result.error, {
          feature: "onboarding",
          action: "cv_draft_write_failed",
          reason: result.isQuota ? "quota" : "unavailable",
        });
        if (result.isQuota) {
          toast.error(
            "Storage full",
            "We couldn’t save your progress locally. Your latest edits may be lost on refresh: try clearing browser data.",
          );
        }
      }
    }, 500);
    return () => {
      if (cvDraftWriteTimerRef.current) {
        clearTimeout(cvDraftWriteTimerRef.current);
        cvDraftWriteTimerRef.current = null;
      }
    };
  }, [cvDraftKey, cvProfile, shouldLoadProfile, toast, user?.id]);

  // Populate CVDataProvider with saved profile data
  useEffect(() => {
    if (!savedProfile) return;

    if (!hasHydratedSavedPrivacyRef.current) {
      hasHydratedSavedPrivacyRef.current = true;
      const privacy = normalizeProfilePrivacySettings(
        (savedProfile as { privacy?: unknown }).privacy,
      );
      setProfileVisibilityState(privacy.profileVisibility);
      setPhotoVisibilityState(privacy.photoVisibility);
      setContactVisibilityState(privacy.contactVisibility);
      setCvVisibilityDefaults(privacy.cvVisibilityDefaults);
    }

    if (hasHydratedSavedProfileRef.current) return;
    hasHydratedSavedProfileRef.current = true;

    updateProfile(savedProfile as any);

    if (
      savedProfile.name ||
      savedProfile.username ||
      savedProfile.avatarUrl ||
      savedProfile.phone ||
      readNationality((savedProfile as Record<string, unknown>).privateInfo)
    ) {
      setProfileDataState((prev) => {
        const nextName = savedProfile.name || prev.name;
        const nextUsername = savedProfile.username || prev.username;
        const nextNationality =
          readNationality(
            (savedProfile as Record<string, unknown>).privateInfo,
          ) || prev.nationality;
        const nextPhone = savedProfile.phone || prev.phone;
        const nextAvatarUrl = savedProfile.avatarUrl || prev.avatarUrl;
        if (
          nextName === prev.name &&
          nextUsername === prev.username &&
          nextNationality === prev.nationality &&
          nextPhone === prev.phone &&
          nextAvatarUrl === prev.avatarUrl
        ) {
          return prev;
        }
        return {
          ...prev,
          name: nextName,
          username: nextUsername,
          nationality: nextNationality,
          phone: nextPhone,
          avatarUrl: nextAvatarUrl,
        };
      });
    }
  }, [savedProfile, updateProfile]);

  // Populate company setup data with saved company
  useEffect(() => {
    if (!savedCompany || hasHydratedSavedCompanyRef.current) return;
    hasHydratedSavedCompanyRef.current = true;

    setCompanyDataState((prev) => {
      const next = {
        ...prev,
        name: savedCompany.name || "",
        slug: savedCompany.slug || "",
        industry: savedCompany.industry || "",
        employeeCount: savedCompany.employeeCount || "",
        location: savedCompany.location || "",
        website: savedCompany.website || "",
        description: savedCompany.description || "",
        logo: savedCompany.logo || "",
      };
      if (
        next.name === prev.name &&
        next.slug === prev.slug &&
        next.industry === prev.industry &&
        next.employeeCount === prev.employeeCount &&
        next.location === prev.location &&
        next.website === prev.website &&
        next.description === prev.description &&
        next.logo === prev.logo
      ) {
        return prev;
      }
      return next;
    });
  }, [savedCompany]);

  useEffect(() => {
    isOnboardingCompletedRef.current = isOnboardingCompleted;
  }, [isOnboardingCompleted]);

  useEffect(() => {
    return () => {
      if (intentTransitionTimerRef.current) {
        clearTimeout(intentTransitionTimerRef.current);
      }
    };
  }, []);

  const waitForCompletedSession = useCallback(async () => {
    for (
      let attempt = 0;
      attempt < COMPLETION_SESSION_MAX_ATTEMPTS;
      attempt += 1
    ) {
      if (isOnboardingCompletedRef.current || hasCompletedAuthSnapshot()) {
        return;
      }

      const refreshedUser = await refreshAuthenticatedUser().catch(() => null);

      if (
        refreshedUser?.isOnboardingCompleted ||
        isOnboardingCompletedRef.current ||
        hasCompletedAuthSnapshot()
      ) {
        return;
      }

      await new Promise((resolve) => {
        setTimeout(resolve, COMPLETION_SESSION_RETRY_MS);
      });
    }

    throw new Error(
      "Finished onboarding, but your session did not refresh. Please try again.",
    );
  }, []);

  const setProfileData = useCallback((data: Partial<ProfileSetupData>) => {
    setProfileDataState((prev) => ({ ...prev, ...data }));
  }, []);

  const setCompanyData = useCallback((data: Partial<CompanySetupData>) => {
    setCompanyDataState((prev) => ({ ...prev, ...data }));
  }, []);

  const setPostOnboardingRoute = useCallback((route: PostOnboardingRoute) => {
    setPostOnboardingRouteState(route);
  }, []);

  const saveProfilePrivacyForStep = useCallback(
    async (step: OnboardingStep) => {
      try {
        await saveProfilePrivacyMutation.mutateAsync({
          ...profilePrivacy,
          publicIndexingAcknowledged:
            profilePrivacy.profileVisibility === "public"
              ? publicIndexingAcknowledged
              : undefined,
          consentSource: "onboarding",
        });
        trackOnboardingEvent("onboarding_step_saved", {
          step,
          properties: {
            saveTarget: "profile_privacy",
            profileVisibility: profilePrivacy.profileVisibility,
            photoVisibility: profilePrivacy.photoVisibility,
            emailVisibility: profilePrivacy.contactVisibility.email,
            phoneVisibility: profilePrivacy.contactVisibility.phone,
          },
        });
      } catch (err) {
        trackOnboardingEvent("onboarding_step_save_failed", {
          step,
          properties: {
            saveTarget: "profile_privacy",
            ...getErrorObservabilityProperties(err),
          },
        });
        captureProductException(err, {
          feature: "onboarding",
          action: "save_profile_privacy",
          step,
        });
        toast.error(
          "Save failed",
          err instanceof Error
            ? err.message
            : "Could not save your privacy settings",
        );
        throw err;
      }
    },
    [
      profilePrivacy,
      publicIndexingAcknowledged,
      saveProfilePrivacyMutation,
      toast,
      trackOnboardingEvent,
    ],
  );

  const setIntent = useCallback(
    (newIntent: OnboardingIntent) => {
      const launchIntent = SEEKER_ONLY_LAUNCH
        ? DEFAULT_SEEKER_ONBOARDING_INTENT
        : newIntent;

      setIntentState(launchIntent);
      if (launchIntent) {
        setPostOnboardingRouteState(getDefaultDestination(launchIntent));
        persistIntent(launchIntent);
      }
    },
    [persistIntent],
  );

  const selectIntentAndAdvance = useCallback(
    (selectedIntent: OnboardingIntent) => {
      const launchIntent = SEEKER_ONLY_LAUNCH
        ? DEFAULT_SEEKER_ONBOARDING_INTENT
        : selectedIntent;
      if (!launchIntent || isIntentTransitioning) return;

      setIsIntentTransitioning(true);
      setIntentState(launchIntent);
      setPostOnboardingRouteState(getDefaultDestination(launchIntent));
      persistIntent(launchIntent);

      const flow = getFlowSteps(launchIntent);
      const currentInFlow = flow.indexOf(activeStepName);
      const intentIndex = flow.indexOf("intent-select");
      const sourceIndex =
        currentInFlow >= 0 ? currentInFlow : Math.max(intentIndex, 0);
      const nextName = flow[Math.min(sourceIndex + 1, flow.length - 1)];

      trackOnboardingEvent("onboarding_intent_selected", {
        step: "intent-select",
        intent: launchIntent,
        properties: {
          selectedIntent: launchIntent,
          destinationStep: nextName,
          destination: getDefaultDestination(launchIntent),
        },
      });
      trackOnboardingEvent("onboarding_step_completed", {
        step: "intent-select",
        intent: launchIntent,
        properties: {
          completionAction: "select_intent",
          destinationStep: nextName,
        },
      });

      goToStep(nextName, {
        mode: isWeb ? "push" : "replace",
        updateWebProgress: true,
        flowIntent: launchIntent,
      });

      if (intentTransitionTimerRef.current) {
        clearTimeout(intentTransitionTimerRef.current);
      }
      intentTransitionTimerRef.current = setTimeout(() => {
        setIsIntentTransitioning(false);
        intentTransitionTimerRef.current = null;
      }, 250);
    },
    [
      activeStepName,
      goToStep,
      isIntentTransitioning,
      persistIntent,
      trackOnboardingEvent,
    ],
  );

  // Keep the legacy enrichment API compatible for old resumed sessions, but
  // new onboarding always starts from one neutral default. It no longer
  // guesses a career stage or launches a Skill automatically.
  const suggestedStyleSegmentId = DEFAULT_CV_STYLE_SEGMENT_ID;

  // Monotonic "furthest step visited" watermark across BOTH platforms -
  // native's activeStepName goes backwards on back-navigation, and optional
  // steps (certifications/highlights) aren't in the linear flow at all, so
  // neither is safe to compare against directly.
  const maxReachedFlowIdxRef = useRef(-1);
  useEffect(() => {
    const flow = getFlowSteps(effectiveIntent) as readonly OnboardingStep[];
    const candidates = isWeb
      ? [webMaxReachedStep, activeStepName]
      : [activeStepName];
    for (const step of candidates) {
      const idx = flow.indexOf(step);
      if (idx > maxReachedFlowIdxRef.current) {
        maxReachedFlowIdxRef.current = idx;
      }
    }
  }, [activeStepName, effectiveIntent, webMaxReachedStep]);

  // Baseline snapshots of the RAW import per draft section. Enrichment only
  // replaces sections the user has not personally edited: a section whose
  // draft still matches its import baseline is safe to upgrade no matter which
  // step the user is on; a touched section is never overwritten.
  const enrichmentBaselineRef = useRef<Record<string, string> | null>(null);
  const sectionSnapshot = (value: unknown) => JSON.stringify(value ?? null);
  const captureEnrichmentBaseline = useCallback(
    (replacement: Record<string, unknown>) => {
      enrichmentBaselineRef.current = {
        experience: sectionSnapshot(replacement.experience),
        education: sectionSnapshot(replacement.education),
        languages: sectionSnapshot(replacement.languages),
        skillGroups: sectionSnapshot(replacement.skillGroups),
        summaryBlock: sectionSnapshot({
          summary: replacement.summary,
          headline: replacement.headline,
          highlights: replacement.highlights,
        }),
        certifications: sectionSnapshot(replacement.certifications),
      };
    },
    [],
  );

  /**
   * Hydrate enriched sections into the local draft without ever clobbering the
   * user's own edits. Preferred check: the import baseline: apply a section
   * only while its draft is still byte-identical to the raw import. Fallback
   * (baseline lost, e.g. page reload): only sections strictly beyond the
   * furthest step the user has visited.
   */
  const applyEnrichedCv = useCallback(
    (parsed: ParsedCvData) => {
      const replacement = buildCvProfileReplacementFromParsed(parsed);
      const flow = getFlowSteps(effectiveIntent) as readonly OnboardingStep[];
      const baseline = enrichmentBaselineRef.current;
      const draft = profileRef.current ?? cvProfile;
      const reachedIdx = maxReachedFlowIdxRef.current;

      const positionAllows = (step: OnboardingStep) => {
        const stepIdx = flow.indexOf(step);
        // Unknown position → be conservative, never clobber.
        if (stepIdx < 0 || reachedIdx < 0) return false;
        return reachedIdx < stepIdx;
      };
      const canApply = (
        step: OnboardingStep,
        key: string,
        currentValue: unknown,
      ) => {
        if (baseline) {
          return baseline[key] === sectionSnapshot(currentValue);
        }
        return positionAllows(step);
      };

      // NOTE: identity/contact fields (name, email, phone, links) are owned by
      // the basics screens and are deliberately never touched here.
      const patch: Record<string, unknown> = {};
      const applied: Record<string, string> = {};
      if (canApply("experience", "experience", draft.experience)) {
        patch.experience = replacement.experience;
        applied.experience = sectionSnapshot(replacement.experience);
      }
      if (canApply("education", "education", draft.education)) {
        patch.education = replacement.education;
        applied.education = sectionSnapshot(replacement.education);
      }
      if (canApply("languages", "languages", draft.languages)) {
        patch.languages = replacement.languages;
        applied.languages = sectionSnapshot(replacement.languages);
      }
      if (canApply("skills", "skillGroups", draft.skillGroups)) {
        patch.skillGroups = replacement.skillGroups;
        applied.skillGroups = sectionSnapshot(replacement.skillGroups);
      }
      if (
        canApply("summary", "summaryBlock", {
          summary: draft.summary,
          headline: draft.headline,
          highlights: draft.highlights,
        })
      ) {
        patch.summary = replacement.summary;
        patch.headline = replacement.headline;
        patch.highlights = replacement.highlights;
        applied.summaryBlock = sectionSnapshot({
          summary: replacement.summary,
          headline: replacement.headline,
          highlights: replacement.highlights,
        });
      }
      // Certifications are only editable from the complete-screen checklist.
      if (canApply("complete", "certifications", draft.certifications)) {
        patch.certifications = replacement.certifications;
        applied.certifications = sectionSnapshot(replacement.certifications);
      }

      if (Object.keys(patch).length > 0) {
        updateProfile(patch);
        // Re-baseline the applied sections so a later style re-run may upgrade
        // them again (they are still "untouched by the user").
        if (baseline) {
          enrichmentBaselineRef.current = { ...baseline, ...applied };
        }
      }
    },
    [cvProfile, effectiveIntent, profileRef, updateProfile],
  );

  // Refs so long-running enrichment always reads/applies against the LATEST
  // state: the .then closure must not consult values frozen at kick-off time.
  const applyEnrichedCvRef = useRef(applyEnrichedCv);
  applyEnrichedCvRef.current = applyEnrichedCv;
  const cvEnrichmentStateRef = useRef(cvEnrichment);
  cvEnrichmentStateRef.current = cvEnrichment;

  const setCvEnrichmentTracked = useCallback((next: CvEnrichmentState) => {
    cvEnrichmentStateRef.current = next;
    setCvEnrichment(next);
  }, []);

  const startCvEnrichment = useCallback(
    (options?: StartCvEnrichmentOptions) => {
      const styleSegmentId =
        options?.styleSegmentId ??
        pendingEnrichmentStyleIdRef.current ??
        suggestedStyleSegmentId;
      setSelectedCvStyleSegmentId(styleSegmentId);

      // Don't restart an identical run; a different style always re-runs, and
      // a completed NON-premium run also re-runs (the server decides the
      // tier, so a premium rewrite must get its chance; a completed premium
      // run can't improve).
      const importGeneration = cvImportGenerationRef.current;
      // An import that went to CV Health is changed only by its fix: the
      // targeted, validated changes it promised. A full rewrite there could
      // lower the score the user was shown (owner testing, 2026-10-04: 92 →
      // 86 after "Fix my CV" on Free). A paid pick on the chooser upgrades
      // the model the fix drafts with (the server reads the verified plan);
      // it never rewrites the CV.
      const cvHealth = cvHealthStateRef.current;
      if (options?.paidUpgrade && cvHealth.import && !cvHealth.skipped) {
        return;
      }
      if (
        options?.paidUpgrade &&
        paidEnrichmentImportRef.current === importGeneration
      ) {
        return;
      }
      const current = cvEnrichmentStateRef.current;
      const sameRun =
        current.styleSegmentId === styleSegmentId &&
        cvEnrichmentRunImportRef.current === importGeneration;
      // A paid upgrade supersedes a free run still in flight (the runId bump
      // below drops its result); waiting for it would leave the CV on the
      // free rewrite.
      if (current.status === "running" && sameRun && !options?.paidUpgrade) {
        return;
      }
      if (current.status === "done" && sameRun && current.premium) {
        return;
      }

      if (options?.paidUpgrade) {
        paidEnrichmentImportRef.current = importGeneration;
      }
      cvEnrichmentRunImportRef.current = importGeneration;
      const runId = ++cvEnrichmentRunRef.current;
      setCvEnrichmentTracked({
        status: "running",
        premium: false,
        styleSegmentId,
      });
      trackProductEvent("onboarding_cv_enrich_started", {
        style: styleSegmentId,
        explicitStyle: Boolean(options?.styleSegmentId),
      });

      void enrichCv({ styleSegmentId })
        .then((result) => {
          if (cvEnrichmentRunRef.current !== runId) return;
          applyEnrichedCvRef.current(result.enriched);
          // A skill style's declared look comes back with the enrichment
          // (authoritative, manifest-derived). Applying it here covers picks
          // that arrived without a layout: a share-link skill claimed with
          // the resume, or a resumed session before the catalog loads.
          if (result.presetLayout) {
            setAppliedPresetLayout(result.presetLayout);
          }
          setCvEnrichmentTracked({
            status: "done",
            premium: result.premium,
            styleSegmentId: result.styleSegmentId ?? styleSegmentId,
          });
        })
        .catch((error) => {
          if (cvEnrichmentRunRef.current !== runId) return;
          if (
            error instanceof OnboardingApiError &&
            (error.code === "NO_RESUME_SOURCE" ||
              error.code === "ENRICH_SUPERSEDED")
          ) {
            // Manual-build path (nothing imported): nothing to enrich. Or a
            // newer upload replaced the CV this run rewrote, so the server
            // dropped it on purpose: no failure, nothing to report.
            setCvEnrichmentTracked({
              status: "idle",
              premium: false,
              styleSegmentId: null,
            });
            return;
          }
          captureProductException(error, {
            feature: "onboarding",
            action: "enrich_cv",
          });
          // Non-blocking: the raw import remains usable; user can retry via
          // the style picker.
          setCvEnrichmentTracked({
            status: "failed",
            premium: false,
            styleSegmentId,
          });
        });
    },
    [setCvEnrichmentTracked, suggestedStyleSegmentId],
  );

  const ensureCvEnrichmentStarted = useCallback((): boolean => {
    // A run that belongs to this import (the chooser's paid re-run, the kick
    // at import for a skipped check, an earlier call of this) is the one run
    // this import gets: Try again, a reload mid-fix and a paid pick landing
    // beside a Free kick must never start a second writing pass.
    if (cvEnrichmentRunImportRef.current !== cvImportGenerationRef.current) {
      startCvEnrichment();
    }
    return cvEnrichmentStateRef.current.status === "running";
  }, [startCvEnrichment]);

  // ─── CV presets + catalog skills (AI-input screen) ────────────
  //
  // A pick is a `styleSegmentId` the user chose by name (a curated preset id
  // or `skill:<skillId>` for a real catalog CV skill), so the whole feature
  // rides the enrichment pipeline that already exists. It is stored as a
  // preference immediately (so a resumed session keeps it) and consumed after
  // the import parses, in `persistImportedCvAndGoToHeader`.
  const cvPresetId =
    isCvPresetId(selectedCvStyleSegmentId) ||
    isSkillStyleId(selectedCvStyleSegmentId)
      ? selectedCvStyleSegmentId
      : null;
  // Read by the import handler, which runs long after the pick: a ref keeps it
  // reading the LATEST choice without re-creating that callback on every pick.
  const cvPresetIdRef = useRef(cvPresetId);
  cvPresetIdRef.current = cvPresetId;
  const cvPresetLayout = useMemo<CvPresetLayout>(
    () =>
      (cvPresetId ? appliedPresetLayout : null) ??
      getCvPresetById(cvPresetId)?.layout ??
      DEFAULT_CV_PRESET_LAYOUT,
    [cvPresetId, appliedPresetLayout],
  );
  const cvPresetLayoutRef = useRef(cvPresetLayout);
  cvPresetLayoutRef.current = cvPresetLayout;
  // The LIVE config the preview renders: the preset seed plus anything the user
  // changed in the Theme sheet. This, not the raw preset, is what gets saved.
  const cvConfigRef = useRef(cvConfig);
  cvConfigRef.current = cvConfig;

  const applyCvPreset = useCallback(
    async (presetId: string, layout?: CvPresetLayout) => {
      const preset = getCvPresetById(presetId);
      const isSkill = isSkillStyleId(presetId);
      if (!preset && !isSkill) return;
      const resolvedLayout =
        layout ?? preset?.layout ?? DEFAULT_CV_PRESET_LAYOUT;

      // Optimistic: the chip must appear immediately even if the preference
      // write is slow. A failed write only costs the pick on a resumed
      // session, so it must never block the composer.
      setSelectedCvStyleSegmentId(presetId);
      pendingEnrichmentStyleIdRef.current = presetId;
      setAppliedPresetLayout(resolvedLayout);
      setIsApplyingCvPreset(true);
      trackProductEvent("onboarding_cv_preset_applied", { preset: presetId });
      try {
        await saveOnboardingPreferences({
          styleSegmentId: presetId,
          presetLayout: resolvedLayout,
        });
      } catch (error) {
        captureProductException(error, {
          feature: "onboarding",
          action: "apply_cv_preset",
          preset: presetId,
        });
      } finally {
        setIsApplyingCvPreset(false);
      }
    },
    [],
  );

  /**
   * Dismissing the chip has to reach the server, not just React.
   *
   * `applyCvPreset` persists the pick so a resumed session keeps it, and the
   * enrich endpoint falls back to that stored value when the request carries
   * no style. So a clear that only touched local state left the pick alive in
   * two places: the chip returned on the next sign-in, and the CV was still
   * enriched with the skill in the meantime. Mirror the apply: clear locally
   * first so the chip goes at once, then write the clear through.
   */
  const clearCvPreset = useCallback(() => {
    const hadPick =
      isCvPresetId(cvPresetIdRef.current) ||
      isSkillStyleId(cvPresetIdRef.current);
    setSelectedCvStyleSegmentId((current) =>
      isCvPresetId(current) || isSkillStyleId(current) ? null : current,
    );
    pendingEnrichmentStyleIdRef.current = null;
    setAppliedPresetLayout(null);
    trackProductEvent("onboarding_cv_preset_cleared", {});
    if (!hadPick) return;
    void saveOnboardingPreferences({
      styleSegmentId: null,
      presetLayout: null,
    })
      .then(() => {
        // The resume effect reads this query; without the refresh a refetch on
        // window focus restores the pick this call just deleted.
        void queryClient.invalidateQueries({
          queryKey: ["onboarding", "state"],
        });
      })
      .catch((error) => {
        captureProductException(error, {
          feature: "onboarding",
          action: "clear_cv_preset",
        });
      });
  }, [queryClient]);

  // Legacy explicit enrichment state remains persistable for compatibility.
  // New onboarding never starts or restores an automatic run.
  useEffect(() => {
    if (!isWeb || !user?.id) return;
    const key = `tabbio:onboarding:cv-enrichment:${user.id}`;
    if (cvEnrichment.status === "idle") {
      removeWebStorage(key);
    } else {
      writeWebStorage(key, JSON.stringify(cvEnrichment));
    }
  }, [cvEnrichment, user?.id]);

  const enrichmentRestoredRef = useRef(false);
  useEffect(() => {
    if (!isWeb || enrichmentRestoredRef.current || !user?.id) return;
    enrichmentRestoredRef.current = true;
    removeWebStorage(`tabbio:onboarding:cv-enrichment:${user.id}`);
  }, [user?.id]);

  // Auto-save current step data before advancing. Awaitable so callers can
  // gate navigation/UI-confirmation on real persistence, not a fire-and-forget
  // mutation that lied about success.
  /** Saves one step's form data; `saveCurrentStepData` is the rendered step's. */
  const saveStepData = useCallback(async (stepName: OnboardingStep): Promise<void> => {
    if (!stepName) return;

    switch (stepName) {
      // this-is-you persists its name via useSaveProfileSetup inside the screen
      // (like the old profile-setup did), so there's nothing to flush here.
      case "this-is-you":
      case "company-setup":
      case "company-next-action":
      case "ai-input":
      case "cv-health":
      case "choose-ai":
      case "complete":
      case "where-heard":
      case "paywall":
      case "intent-select":
        return;
      // who-sees-cv writes the profile/contact visibility settings.
      case "who-sees-cv":
        await saveProfilePrivacyForStep(stepName);
        return;
      case "how-to-reach-you":
      case "education":
      case "experience":
      case "skills":
      case "languages":
      case "certifications":
      case "highlights":
      case "summary": {
        const sections = buildSaveSectionsFromCvProfile(
          profileRef.current ?? cvProfile,
          {
            // The claim flow starts by CREATING a CV row (the claimed copy), so
            // from here on the server's replacement guard rejects every
            // save-sections call that does not confirm. The user is editing the
            // CV they just accepted, so confirming is exactly their intent -
            // and without it the preview's section editors could neither save
            // nor be left (see prevStep). Only this flow opts in: the guard
            // stays intact for the post-onboarding imports it was written for.
            replacementConfirmed: cvClaimFlowRef.current,
          },
        );

        if (Object.keys(sections).length === 0) return;

        try {
          await saveSectionsMutation.mutateAsync(sections);
          trackOnboardingEvent("onboarding_step_saved", {
            step: stepName,
            properties: {
              saveTarget: "cv_sections",
              ...getSaveSectionsAnalytics(sections),
            },
          });
        } catch (err) {
          // Surface the real failure in dev consoles: the toast only shows a
          // generic title, which made client-side payload crashes invisible.
          console.error("[onboarding] save-sections failed", stepName, err);
          trackOnboardingEvent("onboarding_step_save_failed", {
            step: stepName,
            properties: {
              saveTarget: "cv_sections",
              ...getErrorObservabilityProperties(err),
            },
          });
          captureProductException(err, {
            feature: "onboarding",
            action: "save_cv_sections",
            step: stepName,
          });
          toast.error(
            "Save failed",
            err instanceof Error ? err.message : "Could not save your progress",
          );
          throw err;
        }
        return;
      }
      case "job-preferences": {
        const prefs = (profileRef.current ?? cvProfile).jobPreferences;
        try {
          await saveJobPreferencesMutation.mutateAsync({
            openToWork: prefs.openToWork,
            jobTitles: prefs.jobTitles,
            locationTypes: prefs.locationTypes,
            preferredLocations: prefs.preferredLocations,
            employmentTypes: prefs.employmentTypes,
            searchAreas: prefs.searchAreas,
            relocation: prefs.relocation,
            relocationAreas: prefs.relocationAreas,
            noticePeriod: prefs.noticePeriod,
          });
          trackOnboardingEvent("onboarding_step_saved", {
            step: stepName,
            properties: {
              saveTarget: "job_preferences",
              openToWork: Boolean(prefs.openToWork),
              jobTitleCount: prefs.jobTitles?.length ?? 0,
              locationTypeCount: prefs.locationTypes?.length ?? 0,
              preferredLocationCount: prefs.preferredLocations?.length ?? 0,
              employmentTypeCount: prefs.employmentTypes?.length ?? 0,
              searchAreaCount: prefs.searchAreas?.length ?? 0,
            },
          });
        } catch (err) {
          trackOnboardingEvent("onboarding_step_save_failed", {
            step: stepName,
            properties: {
              saveTarget: "job_preferences",
              ...getErrorObservabilityProperties(err),
            },
          });
          captureProductException(err, {
            feature: "onboarding",
            action: "save_job_preferences",
            step: stepName,
          });
          toast.error(
            "Save failed",
            err instanceof Error
              ? err.message
              : "Could not save your preferences",
          );
          throw err;
        }
        return;
      }
    }
  }, [
    cvProfile,
    profileRef,
    saveJobPreferencesMutation,
    saveProfilePrivacyForStep,
    saveSectionsMutation,
    toast,
    trackOnboardingEvent,
  ]);
  const saveCurrentStepData = useCallback(
    (): Promise<void> => saveStepData(activeStepName),
    [activeStepName, saveStepData],
  );

  // Written after nextStep is declared (see the assignment below its
  // definition); a ref, like applyEnrichedCvRef, so nextStep can finish the
  // claim flow without reordering the two callbacks.
  const completeOnboardingRef = useRef<(() => Promise<void>) | null>(null);

  // `rawOptions` is `unknown` on purpose: existing callers pass nextStep
  // straight to onPress/onSave, so an event or form data can arrive here.
  // readNextStepOptions keeps only the known option values. The context types
  // it as NextStepFn (no arguments, or NextStepOptions).
  const nextStep = useCallback(async (rawOptions?: unknown) => {
    const options = readNextStepOptions(rawOptions);
    // The step the user is on NOW. A screen can hold a nextStep from the
    // render before its route took over (choose-ai's one-time mount effect
    // passes a paid user on that way), whose `activeStepName` is still the
    // step before it. On web the URL is always current, so read it.
    const activeStepName = readLiveStepName(activeStepNameFromRender);
    const flow = getFlowSteps(effectiveIntent, flowOptions());
    if (isWeb && options.fromStep) {
      // The caller named its step: it is current exactly while the address
      // bar shows it, whatever render its nextStep came from (choose-ai's
      // mount effect after a Back from this-is-you holds a this-is-you one).
      if (options.fromStep !== activeStepName) return;
    } else {
      // A caller that went stale the other way: the user went Back while its
      // await ran (browser Back during this-is-you's save), so the address
      // bar shows an earlier step than the screen that called. Completing
      // either step would push them forward again past the step they chose:
      // stay.
      const liveIndex = flow.indexOf(activeStepName);
      const renderedIndex = flow.indexOf(activeStepNameFromRender);
      if (liveIndex >= 0 && renderedIndex >= 0 && liveIndex < renderedIndex) {
        return;
      }
    }
    // CV Health's "Fix my CV" goes through the soft plan chooser and comes
    // back: every way out of choose-ai (a plan, Continue free, the X) returns
    // to cv-health to apply the pending fix, and every other exit of the
    // report (Skip, Continue after the fix, a failed check) goes around the
    // chooser: it is only ever shown for the fix, once. The fix request
    // itself is read off the completion action: the step records the pending
    // fix in the same tick it calls here, before the ref below sees it.
    const cvHealthRoute = {
      pendingFix:
        cvHealthStateRef.current.pendingFix ||
        options.completionAction === "fix_with_ai",
      chooserDone: cvHealthStateRef.current.chooserDone,
      skipped: cvHealthStateRef.current.skipped,
    };
    const nextName =
      resolveCvHealthNextStep(flow, activeStepName, cvHealthRoute) ??
      resolveNextOnboardingStep(flow, activeStepName);

    // who-sees-cv is the last step of every seeker flow (imported, manual
    // and claimed CV alike: no survey, no paywall after it), so advancing
    // past it IS completion. completeOnboarding runs its own
    // saveCurrentStepData (the privacy write for who-sees-cv), so don't
    // double-save here; the step-completed event is what the where-heard
    // terminal used to emit, so the funnel keeps a step after `complete`.
    if (nextName === activeStepName && activeStepName === PRIVACY_STEP) {
      trackOnboardingEvent("onboarding_step_completed", {
        step: activeStepName,
        properties: {
          completionAction: options.completionAction ?? "next",
          destinationStep: "complete",
          ...getStepCompletionAnalytics(
            activeStepName,
            profileData,
            companyData,
            (profileRef.current ?? cvProfile) as Record<string, any>,
          ),
        },
      });
      await completeOnboardingRef.current?.();
      return;
    }

    if (activeStepName === "this-is-you") {
      setHasLocallyCompletedProfileSetup(true);

      if (
        SEEKER_ONLY_LAUNCH &&
        intent !== DEFAULT_SEEKER_ONBOARDING_INTENT &&
        rawSavedIntent !== DEFAULT_SEEKER_ONBOARDING_INTENT
      ) {
        // Limited launch: keep the old intent screen available in code, but
        // persist seeker intent here (this-is-you is the new "profile" step) so
        // the user continues straight through the CV flow.
        setIntentState(DEFAULT_SEEKER_ONBOARDING_INTENT);
        setPostOnboardingRouteState(
          getDefaultDestination(DEFAULT_SEEKER_ONBOARDING_INTENT),
        );
        persistIntent(DEFAULT_SEEKER_ONBOARDING_INTENT);
        trackOnboardingEvent("onboarding_intent_selected", {
          step: "intent-select",
          intent: DEFAULT_SEEKER_ONBOARDING_INTENT,
          properties: {
            selectedIntent: DEFAULT_SEEKER_ONBOARDING_INTENT,
            selectionSource: "limited_launch_default",
            destinationStep: nextName,
            destination: getDefaultDestination(
              DEFAULT_SEEKER_ONBOARDING_INTENT,
            ),
          },
        });
      }
    }
    if (!isWeb && stickyNativeEntryStepRef.current === activeStepName) {
      stickyNativeEntryStepRef.current = null;
      if (!postAuthTargetSurvivesOnboarding(readPostAuthTarget())) {
        clearPostAuthTarget();
      }
    }
    try {
      await saveStepData(activeStepName);
    } catch {
      // saveCurrentStepData already toasted + tracked. Don't advance so the
      // "Saved" indicator never appears for a state that never persisted.
      return;
    }
    trackOnboardingEvent("onboarding_step_completed", {
      step: activeStepName,
      properties: {
        completionAction: options.completionAction ?? "next",
        destinationStep: nextName,
        ...getStepCompletionAnalytics(
          activeStepName,
          profileData,
          companyData,
          (profileRef.current ?? cvProfile) as Record<string, any>,
        ),
      },
    });
    const returningToCvHealth =
      activeStepName === "choose-ai" && nextName === "cv-health";
    if (returningToCvHealth) {
      // Back to the report with the fix to apply.
      setCvHealthState((current) => ({
        ...current,
        phase: "fixing",
        chooserDone: true,
      }));
    } else if (activeStepName === "choose-ai") {
      // Any other way past the chooser (a plan, Free, a paid user passed on)
      // also counts: it never shows twice.
      setCvHealthState((current) =>
        current.chooserDone ? current : { ...current, chooserDone: true },
      );
    } else if (
      activeStepName === "cv-health" &&
      options.completionAction !== undefined &&
      options.completionAction !== "fix_with_ai"
    ) {
      // Leaving the report any other way drops a pending fix (a browser Back
      // from the chooser, then Skip, must not fix later). Only the report's
      // own exits count: each passes its completion action, and a call
      // without one is never the report leaving.
      setCvHealthState((current) =>
        current.pendingFix || current.phase === "fixing"
          ? {
              ...current,
              pendingFix: false,
              phase: current.fix ? "fixed" : "report",
            }
          : current,
      );
      if (options.completionAction === "skip_disabled") {
        setCvHealthState((current) =>
          current.skipped ? current : { ...current, skipped: true },
        );
      }
    }
    // Back from the chooser to the report it came from: step back through
    // the history, so browser Back from the saved report never shows the
    // chooser again (and leaves no duplicate report entry either). A caller
    // asking to replace the chooser's entry wants the same thing.
    if (
      returningToCvHealth &&
      options.historyMode !== "push" &&
      returnByHistoryBack(nextName)
    ) {
      return;
    }
    goToStep(nextName, {
      mode: options.historyMode ?? (isWeb ? "push" : "replace"),
      updateWebProgress: true,
      flowIntent: effectiveIntent,
    });
  }, [
    activeStepNameFromRender,
    returnByHistoryBack,
    readLiveStepName,
    companyData,
    cvProfile,
    effectiveIntent,
    goToStep,
    intent,
    rawSavedIntent,
    persistIntent,
    profileData,
    profileRef,
    saveStepData,
    trackOnboardingEvent,
    flowOptions,
  ]);

  // Skip the current optional step: advance to the next flow step WITHOUT
  // saving the form. Used by the Skip action on optional CV sections
  // (languages / skills / summary / job-preferences) where no info is required.
  // Existing provider data (e.g. from AI import) is untouched: only the
  // current screen's unsaved edits are dropped.
  const skipStep = useCallback(() => {
    const flow = getFlowSteps(effectiveIntent, flowOptions());
    const nextName = resolveNextOnboardingStep(flow, activeStepName);

    trackOnboardingEvent("onboarding_step_completed", {
      step: activeStepName,
      properties: {
        completionAction: "skip",
        destinationStep: nextName,
      },
    });
    goToStep(nextName, {
      mode: isWeb ? "push" : "replace",
      updateWebProgress: true,
      flowIntent: effectiveIntent,
    });
  }, [activeStepName, effectiveIntent, goToStep, trackOnboardingEvent, flowOptions]);

  // Legacy jump into the CV basics (no external callers today). The import
  // path itself lands on cv-health (then choose-ai behind "Fix my CV"); see
  // persistImportedCvAndGoToHeader.
  const goToCvHeader = useCallback(() => {
    // Save inside already toasts + tracks failures; outer catch only
    // silences the unhandled-rejection warning.
    void saveCurrentStepData().catch(() => {});
    setCvEntryMode("imported");
    trackOnboardingEvent("onboarding_step_completed", {
      step: activeStepName,
      properties: {
        completionAction: "cv_import_or_manual",
        destinationStep: "this-is-you",
        ...getCvProfileAnalytics(
          (profileRef.current ?? cvProfile) as Record<string, any>,
        ),
      },
    });
    goToStep("this-is-you", {
      mode: isWeb ? "replace" : "replace",
      updateWebProgress: true,
      flowIntent: effectiveIntent ?? "job",
    });
  }, [
    activeStepName,
    cvProfile,
    effectiveIntent,
    goToStep,
    profileRef,
    saveCurrentStepData,
    setCvEntryMode,
    trackOnboardingEvent,
  ]);

  const startManualCvBuild = useCallback(() => {
    void saveCurrentStepData().catch(() => {});
    // Building manually cancels a pending website resume import (token and
    // its `resume-import` return); shared here so native and web agree. A job
    // the user applied to or a thread they opened is left to be resumed.
    void abandonPendingResumeImport();
    // A failed import reports through both the composer and a global toast.
    // The composer unmounts when manual entry starts; dismiss the global copy
    // as well so a superseded upload error does not follow the user through
    // the manual section flow.
    toast.dismiss();
    setOnboardingError(null);
    setCvEntryMode("manual");
    trackOnboardingEvent("onboarding_step_completed", {
      step: activeStepName,
      properties: {
        completionAction: "manual_cv_build",
        destinationStep: "this-is-you",
        ...getCvProfileAnalytics(
          (profileRef.current ?? cvProfile) as Record<string, any>,
        ),
      },
    });
    goToStep("this-is-you", {
      mode: isWeb ? "push" : "replace",
      updateWebProgress: true,
      flowIntent: effectiveIntent ?? "job",
    });
  }, [
    activeStepName,
    cvProfile,
    effectiveIntent,
    goToStep,
    profileRef,
    saveCurrentStepData,
    setCvEntryMode,
    toast,
    trackOnboardingEvent,
  ]);

  /**
   * A pending CV-handoff invite was claimed on the ai-input step with intent
   * 'update' (the server hydrated the new account's profile from the delivered
   * CV). Switch to the short claim flow and jump straight to the CV preview:
   * ai-input -> complete -> this-is-you -> who-sees-cv, then completion (see
   * CLAIMED_CV_FLOW_STEPS in onboarding-navigation.ts).
   */
  const enterClaimedCvFlow = useCallback(() => {
    setCvClaimFlow(true);
    // The claimed content behaves like an import: keeps the manual section
    // wizard out of the flow and Back from this-is-you linear.
    setCvEntryMode("imported");
    trackOnboardingEvent("onboarding_step_completed", {
      step: "ai-input",
      properties: {
        completionAction: "cv_handoff_claim",
        destinationStep: "complete",
      },
    });
    goToStep("complete", {
      mode: "replace",
      updateWebProgress: true,
      flowIntent: effectiveIntent ?? "job",
    });
  }, [
    effectiveIntent,
    goToStep,
    setCvClaimFlow,
    setCvEntryMode,
    trackOnboardingEvent,
  ]);

  const isClaimedCvFlow = useCallback(() => cvClaimFlowRef.current, []);

  const persistImportedCvAndGoToHeader = useCallback(
    async (
      parsed: ParsedCvData,
      options?: {
        mode?: NavigationMode;
        /**
         * Style to enrich with, when the caller knows it before React has
         * re-rendered (a share-link skill claimed with the resume): the ref
         * below only reflects a pick after a render, so a just-applied pick
         * must be passed explicitly to reach this import's enrichment.
         */
        presetStyleId?: string;
        /**
         * Parse quality of this import, handed to the CV Health step as the
         * review's `importSignals` (a header-only parse reads as unreadable).
         */
        quality?: CvParseQuality | null;
      importEvidenceId?: string;
        /** Where the CV came from: the composer's file or text, or the resume handoff. */
        source?: CvHealthImportSource;
        /**
         * The public CV Health report this handoff was made from
         * (cvhealth.tabbio.com or a partner's CV score page), forwarded to
         * the review so onboarding shows that same report (LM1).
         */
        cvHealthToken?: string | null;
      },
    ): Promise<void> => {
      // Client-side twin of the server's replacement guard (incident
      // 2026-09-02): a parse with no CV body never reaches the destructive
      // save, whatever the server answered. The builder below refuses too;
      // this check names the reason in analytics before anything is built.
      const assessment = assessParsedCvForReplacement(parsed);
      if (!assessment.hasMeaningfulEvidence) {
        trackOnboardingEvent("onboarding_step_save_failed", {
          step: "ai-input",
          properties: {
            saveTarget: "parsed_cv_sections",
            errorCode: "CV_PARSE_INCOMPLETE",
            completeness: assessment.verdict,
          },
        });
        throw new ImportedCvIncompleteError(assessment);
      }

      const sections = {
        ...buildSaveSectionsFromParsedCvData(parsed, {
          replaceExisting: true,
        }),
        source: parsed,
        importEvidenceId: options?.importEvidenceId,
      };

      const replacement = buildCvProfileReplacementFromParsed(parsed);
      const importedNationality = parsed.nationality?.trim();

      try {
        await saveSectionsMutation.mutateAsync(sections);
        trackOnboardingEvent("onboarding_step_saved", {
          step: "ai-input",
          properties: {
            saveTarget: "parsed_cv_sections",
            ...getSaveSectionsAnalytics(sections),
          },
        });
      } catch (err) {
        if (isCvImportReviewCancelled(err) || isCvImportSourceChanged(err)) throw err;
        trackOnboardingEvent("onboarding_step_save_failed", {
          step: "ai-input",
          properties: {
            saveTarget: "parsed_cv_sections",
            ...getErrorObservabilityProperties(err),
          },
        });
        captureProductException(err, {
          feature: "onboarding",
          action: "save_parsed_cv_sections",
          step: "ai-input",
        });
        toast.error(
          "Save failed",
          err instanceof Error
            ? err.message
            : "Could not save your imported CV",
        );
        throw err;
      }

      // Commit the parsed draft locally only after the server accepts it.
      // Previously this ran before save-sections, so a rejected upload left
      // invalid imported fields in CVDataProvider. Switching to manual entry
      // then resubmitted those stale fields on every later step and trapped
      // the user behind the original validation error.
      if (importedNationality) {
        replacement.privateInfo = {
          ...(profileRef.current?.privateInfo ?? {}),
          nationality: importedNationality,
        };
        setProfileDataState((current) => ({
          ...current,
          nationality: importedNationality,
        }));
      }
      updateProfile(replacement);
      // The CV's own name replaces the signup placeholder (the email's local
      // part) the provider captured before the upload, so "This Is You" never
      // saves the handle back over it. UAE PASS seeds its own CV name there.
      if (!user?.isUaePassVerified) {
        setProfileDataState((current) => {
          const name = resolveImportedCvName(current.name, parsed.name, user?.email);
          return name === current.name ? current : { ...current, name };
        });
      }
      // Snapshot the raw import per section: background enrichment later only
      // upgrades sections still identical to this baseline (user edits win).
      captureEnrichmentBaseline(replacement as Record<string, unknown>);

      trackOnboardingEvent("onboarding_step_completed", {
        step: "ai-input",
        properties: {
          completionAction: "cv_import",
          destinationStep: "cv-health",
          ...getCvProfileAnalytics(profileRef.current as Record<string, any>),
        },
      });
      setCvEntryMode("imported");

      // A new import gets a new check: forget any report of an earlier one,
      // so the CV Health step starts fresh instead of adopting it.
      const cvHealthImport: CvHealthImportContext = {
        quality: options?.quality ?? null,
        source: options?.source ?? null,
        importedAt: Date.now(),
        cvHealthToken: options?.cvHealthToken ?? null,
      };
      setCvHealthState((current) => ({
        ...current,
        import: cvHealthImport,
        phase: "checking",
        reportId: null,
        importScore: null,
        fix: null,
        pendingFix: false,
        chooserDone: false,
        skipped: false,
      }));

      pendingEnrichmentStyleIdRef.current =
        options?.presetStyleId ?? cvPresetIdRef.current;

      // The writing pass (enrichment) belongs to this import: one run per
      // upload (cvImportGenerationRef). When the CV Health step will check
      // this upload it does NOT start here: the report judges the upload as
      // parsed (`source: 'import'`, ResumeSource.raw), and the writing waits
      // for "Fix my CV", after the plan chooser, at the chosen tier (the
      // server resolves it from the real subscription: Free writes the base
      // pass, a paid pick or a paid account the premium one), so stage 1
      // "Writing your CV…" covers the real writing and the CV the editor
      // opens is the one the fixed report describes. Skip never writes: the
      // CV stays exactly as uploaded, so the editor's report matches the
      // onboarding report the user saw. Only an import with nothing to check
      // (a typed description, see shouldSkipCvHealthForImport) is written
      // right away, as before; the step itself kicks the pass on a
      // feature-off skip and on "Continue without checking".
      cvImportGenerationRef.current += 1;
      if (shouldSkipCvHealthForImport(cvHealthImport)) {
        startCvEnrichment();
      } else {
        // Nothing is running for this upload yet: an earlier upload's result
        // must not read as this one's writing pass.
        setCvEnrichmentTracked({
          status: "idle",
          premium: false,
          styleSegmentId: null,
        });
      }

      goToStep("cv-health", {
        mode: options?.mode ?? (isWeb ? "push" : "replace"),
        updateWebProgress: true,
        flowIntent: effectiveIntent ?? "job",
      });
    },
    [
      captureEnrichmentBaseline,
      effectiveIntent,
      goToStep,
      profileRef,
      saveSectionsMutation,
      setCvEntryMode,
      setCvEnrichmentTracked,
      startCvEnrichment,
      toast,
      trackOnboardingEvent,
      updateProfile,
      user?.email,
      user?.isUaePassVerified,
    ],
  );

  const prevStep = useCallback(async () => {
    const flow = getFlowSteps(effectiveIntent, flowOptions());
    // The manual build never entered the check or the chooser: its Back from
    // this-is-you is the linear (manual) one, straight to the import screen.
    const cvHealthBack =
      cvEntryModeRef.current === "manual"
        ? null
        : resolveCvHealthPreviousStep(flow, activeStepName, {
            pendingFix: cvHealthStateRef.current.pendingFix,
            chooserDone: cvHealthStateRef.current.chooserDone,
            skipped: cvHealthStateRef.current.skipped,
          });
    const prevName =
      cvHealthBack ??
      resolvePreviousOnboardingStep(
        flow,
        activeStepName,
        cvEntryModeRef.current,
      );
    if (activeStepName === "cv-health") {
      // Leaving the step drops a pending fix.
      setCvHealthState((current) =>
        current.pendingFix || current.phase === "fixing"
          ? {
              ...current,
              pendingFix: false,
              phase: current.fix ? "fixed" : "report",
            }
          : current,
      );
    }
    // Persist the complete section snapshot before unmount. Navigating while
    // this request is unresolved used to remount the Experience step from the
    // older backend/provider snapshot, silently dropping newly-added entries.
    // saveCurrentStepData owns the visible error toast.
    //
    // A FAILED save must never block Back, same call as goToStepPublic below.
    // Refusing to leave used to strand the user: a section editor opened from
    // the CV preview has exactly two exits (Save -> nextStep, Back -> prevStep)
    // and both run this same save, so on native - where the whole wizard is one
    // route - a save that keeps failing left force-quitting the app as the only
    // way out. Their edits live in the provider draft, so going back neither
    // loses them nor pretends they were stored: the next save retries the same
    // snapshot.
    try {
      await saveCurrentStepData();
    } catch {
      // Already toasted and tracked. Fall through and navigate anyway.
    }
    trackOnboardingEvent("onboarding_step_back_clicked", {
      step: activeStepName,
      properties: {
        destinationStep: prevName,
      },
    });
    // Web: the entry behind is usually the step Back wants; going back
    // through it leaves no duplicate entry for the next browser Back.
    if (returnByHistoryBack(prevName)) return;
    goToStep(prevName, {
      mode: "replace",
      updateWebProgress: false,
      flowIntent: effectiveIntent,
    });
  }, [
    activeStepName,
    effectiveIntent,
    goToStep,
    returnByHistoryBack,
    saveCurrentStepData,
    trackOnboardingEvent,
    flowOptions,
  ]);

  const skipToComplete = useCallback(() => {
    // Save inside already toasts + tracks failures; outer catch only
    // silences the unhandled-rejection warning.
    void saveCurrentStepData().catch(() => {});
    trackOnboardingEvent("onboarding_step_skipped_to_complete", {
      step: activeStepName,
      properties: {
        destinationStep: "complete",
        ...getStepCompletionAnalytics(
          activeStepName,
          profileData,
          companyData,
          (profileRef.current ?? cvProfile) as Record<string, any>,
        ),
      },
    });
    goToStep("complete", {
      mode: isWeb ? "push" : "replace",
      updateWebProgress: true,
      flowIntent: effectiveIntent,
    });
  }, [
    activeStepName,
    companyData,
    cvProfile,
    effectiveIntent,
    goToStep,
    profileData,
    profileRef,
    saveCurrentStepData,
    trackOnboardingEvent,
  ]);

  // Public step jump (e.g. the "Complete my CV" checklist tapping a section,
  // or an optional CV section like Key Achievements/highlights returning to
  // the complete screen). We AWAIT saveCurrentStepData() before navigating -
  // mirroring nextStep: so edits (e.g. highlights) reliably reach the backend
  // instead of racing the unmount as a swallowed fire-and-forget promise.
  // We deliberately do NOT trap the user on failure: saveCurrentStepData
  // already surfaces an error toast, so we still navigate, then re-throw so the
  // calling form (EditHighlightsForm / EditCertificationsForm) reflects the
  // real result via its t('cv.edit.saveFailed') catch instead of showing a
  // false "Saved". Non-data jumps (the complete-screen checklist navigates FROM
  // the no-op 'complete' step) resolve instantly and never throw.
  const goToStepPublic = useCallback(
    async (step: OnboardingStep) => {
      let saveError: unknown;
      try {
        await saveCurrentStepData();
      } catch (err) {
        saveError = err;
      }
      goToStep(step, {
        mode: isWeb ? "push" : "replace",
        updateWebProgress: true,
        flowIntent: effectiveIntent,
      });
      if (saveError) throw saveError;
    },
    [effectiveIntent, goToStep, saveCurrentStepData],
  );

  const completeOnboarding = useCallback(async () => {
    const startedAt = Date.now();
    const postAuthTarget = await readPostAuthTargetAsync();
    const resumeHandoffToken = await readResumeHandoffTokenAsync();
    const shouldReturnToResumeImport =
      postAuthTarget === "resume-import" && !!resumeHandoffToken;
    if (postAuthTarget === "resume-import" && !resumeHandoffToken) {
      // The website upload behind this sign-in is gone (aged out, or already
      // imported): nothing is left to return to, so the target must not
      // linger for a later replay.
      clearPostAuthTarget();
    }
    // A public "Apply" that required onboarding stashed the job as a jobs:
    // target: resume it so the new applicant lands on the job they applied to
    // (via the unguarded /jobs-handoff shim), not the generic app home.
    const isJobsTarget = postAuthTarget.startsWith("jobs:");
    // Same treatment for a public company-page "Join the talent network" that
    // ran through signup: resume the connect-join dialog after onboarding.
    const isJoinTarget = postAuthTarget.startsWith("join:");
    const isMessagesTarget = postAuthTarget.startsWith("messages:");
    // A shared-CV invite opened by someone with no account signs UP, so the
    // grant rides through onboarding and is resumed here: otherwise completion
    // falls through to the workspace default and shows their OWN CV (#246).
    const isCvGrantTarget = postAuthTarget.startsWith("cv-grant:");
    // A CV-handoff invite normally gets claimed (and its target cleared) on
    // the ai-input step; when onboarding resumed PAST ai-input the token rides
    // to completion instead and is resumed via the /cv-handoff chooser shim.
    const isCvHandoffTarget = postAuthTarget.startsWith("cv-handoff:");
    const isAppHandoffTarget =
      postAuthTarget === "skills-cv" ||
      postAuthTarget.startsWith("skill-run:") ||
      isJobsTarget ||
      isJoinTarget ||
      isMessagesTarget ||
      isCvGrantTarget ||
      isCvHandoffTarget;
    // The claim flow (a handoff claimed with intent 'update' during
    // onboarding): the user's main CV now carries the delivered content, so
    // completion lands on it regardless of what any step set as the default
    // route.
    const isClaimedCvFlow = cvClaimFlowRef.current;
    const effectivePostOnboardingRoute: PostOnboardingRoute = isClaimedCvFlow
      ? SEEKER_COMPLETION_ROUTE
      : postOnboardingRoute;
    // Straight to where the user is headed: a seeker lands on their own CV
    // (the default route), a public job "Apply"/join/message/CV-grant/CV-
    // handoff that ran through onboarding resumes on its target. Nothing
    // borrows the screen first: the chat welcome upsell and the product tour
    // left the completion path (the tour starts on the user's first visit to
    // chat instead), so no destination has to be parked and handed back.
    const completionDestination = shouldReturnToResumeImport
      ? "/resume-import"
      : isAppHandoffTarget
        ? getPostAuthDestinationHref(postAuthTarget)
        : effectivePostOnboardingRoute;
    if (isAppHandoffTarget) {
      // Consume it so it can't leak into a later sign-in; its destination also
      // rides in the root handoff URL, so clearing the stored copy now is safe.
      clearPostAuthTarget();
    }

    setIsCompleting(true);
    setOnboardingError(null);
    trackOnboardingEvent("onboarding_completion_started", {
      step: activeStepName,
      properties: {
        destination: completionDestination,
        ...getStepCompletionAnalytics(
          activeStepName,
          profileData,
          companyData,
          (profileRef.current ?? cvProfile) as Record<string, any>,
        ),
      },
    });

    try {
      try {
        await saveCurrentStepData();
      } catch {
        // saveCurrentStepData already surfaced toast + analytics +
        // captureProductException. Short-circuit without firing the
        // generic onboarding_completion_failed path: a save failure
        // shouldn't be reported as a completion failure.
        setIsCompleting(false);
        return;
      }
      await completeMutation.mutateAsync();
      // Onboarding succeeded: the server now holds the source-of-truth
      // profile, so the SCRUM-276 localStorage draft can be cleared.
      clearStoredCvDraft(cvDraftKey);
      // The claim flow is finished: clear its persisted flag so a later
      // onboarding in the same browser session cannot inherit the short flow.
      if (isClaimedCvFlow) {
        setCvClaimFlow(false);
      }
      // Carry the chosen look onto the real CV: the applied preset plus any
      // manual change made in the preview's Theme sheet (onboarding's
      // CVDataProvider has no save callbacks, so this is the only write).
      // Deliberately after the complete call, which creates the CV row, and
      // deliberately non-fatal: a failed theme write must never strand a user
      // who has finished onboarding, and they can restyle from the CV editor.
      const finalCvConfig = cvConfigRef.current;
      const isCustomised =
        Boolean(cvPresetIdRef.current) ||
        finalCvConfig.style !== DEFAULT_CV_PRESET_LAYOUT.style ||
        finalCvConfig.color !== DEFAULT_CV_PRESET_LAYOUT.color ||
        finalCvConfig.showPhoto !== DEFAULT_CV_PRESET_LAYOUT.showPhoto;
      if (isCustomised) {
        try {
          await saveCvConfig(finalCvConfig);
        } catch (err) {
          captureProductException(err, {
            feature: "onboarding",
            action: "save_cv_preset_config",
            preset: cvPresetIdRef.current,
          });
        }
      }
      // Navigate in the same tick the auth flag flips. Otherwise
      // Stack.Protected unmounts the (onboarding) tree, falls back to
      // app/index.tsx, and redirects to the workspace default (chat) before
      // our replace lands.
      mergeAuthenticatedUser({ isOnboardingCompleted: true });
      setIsCompleting(false);
      router.replace(completionDestination as any);
      completedOnboardingAnalyticsRef.current = true;
      trackOnboardingEvent("onboarding_completed_client", {
        step: activeStepName,
        properties: {
          destination: completionDestination,
          durationMs: Math.max(Date.now() - startedAt, 0),
        },
      });
      // Best-effort session refresh; user has already navigated.
      void waitForCompletedSession().catch((err) =>
        captureProductException(err, {
          feature: "onboarding",
          action: "wait_for_session",
          step: activeStepName,
        }),
      );
    } catch (error) {
      setIsCompleting(false);
      const msg =
        error instanceof Error
          ? error.message
          : "Something went wrong. Please try again.";
      setOnboardingError(msg);
      trackOnboardingEvent("onboarding_completion_failed", {
        step: activeStepName,
        properties: {
          destination: completionDestination,
          durationMs: Math.max(Date.now() - startedAt, 0),
          ...getErrorObservabilityProperties(error),
        },
      });
      captureProductException(error, {
        feature: "onboarding",
        action: "complete",
        step: activeStepName,
        destination: completionDestination,
      });
      toast.error("Onboarding failed", msg);
    }
  }, [
    activeStepName,
    companyData,
    router,
    completeMutation,
    cvDraftKey,
    cvProfile,
    profileData,
    profileRef,
    saveCurrentStepData,
    setCvClaimFlow,
    postOnboardingRoute,
    toast,
    trackOnboardingEvent,
    waitForCompletedSession,
  ]);
  // Keeps nextStep's claim-flow completion seam pointed at the LATEST
  // completeOnboarding without reordering the two callbacks.
  completeOnboardingRef.current = completeOnboarding;

  const finishWhereHeard = useCallback(
    async (action: "next" | "skip") => {
      // where-heard is a PRESERVED step (reached only by an old deep link):
      // it is no longer in the flow, but when it does show, both "next" and
      // "skip" complete onboarding directly and land on the user's CV, as
      // they did when it was the last step. There is no paywall after it.
      trackOnboardingEvent("onboarding_step_completed", {
        step: "where-heard",
        properties: {
          completionAction: action,
          destinationStep: "complete",
        },
      });
      await completeOnboarding();
    },
    [completeOnboarding, trackOnboardingEvent],
  );

  const clearError = useCallback(() => setOnboardingError(null), []);

  const stepName = activeStepName;
  const activeFlowOptions = flowOptions();
  const activeFlowSteps = getFlowSteps(effectiveIntent, activeFlowOptions);
  const preIntentProgress: OnboardingStep[] = ["intent-select", "complete"];
  const visibleProgressSteps =
    !effectiveIntent && stepName === "intent-select"
      ? preIntentProgress.filter((s) => s !== "complete")
      : activeFlowSteps.filter((s) => s !== "complete");
  const progressIndex =
    stepName === "complete"
      ? -1
      : visibleProgressSteps.indexOf(
          stepName as (typeof visibleProgressSteps)[number],
        );
  const cvStepCount = CV_STEPS.length;
  const cvStepIndex = CV_STEPS.includes(stepName as (typeof CV_STEPS)[number])
    ? CV_STEPS.indexOf(stepName as (typeof CV_STEPS)[number])
    : -1;

  // The CV phase for the progress bar: every LINEAR step between the import
  // screen and the preview, read off the live flow rather than a fixed list.
  // With an imported CV that is just the one basics screen (this-is-you); the
  // manual build path splices its contact screen and section wizard in there
  // (insertManualCvSections), and those screens have to move the same bar.
  // Reading the flow is what keeps them on it: a hardcoded list left every
  // manual section step at index -1, i.e. with no progress bar at all. Once
  // the preview has been reached the sections drop out of the flow again (they
  // become off-flow preview editors) and correctly lose the bar.
  const cvPhaseSteps = activeFlowSteps.filter(
    (step) =>
      step !== "ai-input" &&
      step !== "cv-health" &&
      step !== "choose-ai" &&
      step !== COMPLETE_STEP &&
      !TERMINAL_STEPS.includes(step),
  );
  // The basics screens that are linear right now: this-is-you alone for an
  // imported CV, this-is-you then how-to-reach-you on the manual build.
  const cvBasicsSteps = cvPhaseSteps.filter((step) =>
    CV_BASICS_STEPS.includes(step),
  );

  // Phase-scoped progress for the shared segmented bar (plus the web shell's
  // top bar). On native the "basics" screens (this-is-you, and
  // how-to-reach-you on the manual build) own their isolated segmented bar via
  // OnboardingFormHeader, one segment per linear basics screen, so they get
  // the basics count here instead of the whole CV phase; on web they flow
  // through the layout's shared bar so the whole flow reads as one track.
  // Falls back to no bar for ai-input / cv-health / choose-ai / complete, for
  // a basics screen opened from the preview, and for the legacy non-seeker
  // flow.
  const phaseProgress = (() => {
    if (SEEKER_ONLY_LAUNCH) {
      const forced = SEEKER_LAUNCH_FORCED_STEPS as readonly OnboardingStep[];
      const forcedPos = forced.indexOf(stepName);
      if (forcedPos >= 0) return { index: forcedPos, total: forced.length };
      if (!isWeb && CV_BASICS_STEPS.includes(stepName)) {
        const basicsPos = cvBasicsSteps.indexOf(stepName);
        return basicsPos >= 0
          ? { index: basicsPos, total: cvBasicsSteps.length }
          : { index: -1, total: 0 };
      }
      const cvPos = cvPhaseSteps.indexOf(stepName);
      if (cvPos >= 0) return { index: cvPos, total: cvPhaseSteps.length };
      // Every seeker flow ends at who-sees-cv (no survey, no paywall), so
      // the terminal bar is that single step.
      const terminalPos = TERMINAL_STEPS.indexOf(stepName);
      if (terminalPos >= 0)
        return { index: terminalPos, total: TERMINAL_STEPS.length };
    }
    return { index: -1, total: 0 };
  })();
  const phaseIndex = phaseProgress.index;
  const phaseTotal = phaseProgress.total;

  const value = useMemo<OnboardingContextValue>(
    () => ({
      currentStep: isWeb ? activeStep : currentStep,
      stepName,
      totalSteps: ALL_STEPS.length,
      cvStepCount,
      cvStepIndex,
      progressTotal: visibleProgressSteps.length,
      progressIndex,
      phaseTotal,
      phaseIndex,
      profileData,
      setProfileData,
      hasCompletedProfileSetup: mergedProgress.hasProfile,
      profileVisibility,
      setProfileVisibility: setProfileVisibilityState,
      publicIndexingAcknowledged,
      setPublicIndexingAcknowledged,
      photoVisibility,
      setPhotoVisibility: setPhotoVisibilityState,
      contactVisibility,
      setContactVisibility: setContactVisibilityState,
      companyData,
      setCompanyData,
      intent: effectiveIntent,
      setIntent,
      selectIntentAndAdvance,
      isIntentTransitioning,
      postOnboardingRoute,
      setPostOnboardingRoute,
      nextStep,
      goToCvHeader,
      startManualCvBuild,
      enterClaimedCvFlow,
      isClaimedCvFlow,
      persistImportedCvAndGoToHeader,
      goToStep: goToStepPublic,
      prevStep,
      skipStep,
      skipToComplete,
      completeOnboarding,
      finishWhereHeard,
      cvPreviewOpen,
      setCvPreviewOpen,
      pendingDownloadFormat,
      setPendingDownloadFormat,
      cvHealthImport: cvHealthState.import,
      cvHealthPhase: cvHealthState.phase,
      setCvHealthPhase,
      cvHealthReportId: cvHealthState.reportId,
      cvHealthImportScore: cvHealthState.importScore,
      setCvHealthImportReport,
      cvHealthFix: cvHealthState.fix,
      setCvHealthFix,
      refreshCvSectionsFromServer,
      cvHealthPendingFix: cvHealthState.pendingFix,
      setCvHealthPendingFix,
      cvHealthChooserDone: cvHealthState.chooserDone,
      markCvHealthChooserDone,
      cvEnrichment,
      selectedCvStyleSegmentId,
      startCvEnrichment,
      ensureCvEnrichmentStarted,
      suggestedStyleSegmentId,
      cvPresetId,
      cvPresetLayout,
      applyCvPreset,
      clearCvPreset,
      isApplyingCvPreset,
      isCompleting,
      onboardingError,
      clearError,
      isLoadingState: isLoadingState || isLoadingProfile || isLoadingCompany,
    }),
    [
      activeStep,
      currentStep,
      stepName,
      cvStepCount,
      cvStepIndex,
      visibleProgressSteps.length,
      progressIndex,
      phaseTotal,
      phaseIndex,
      profileData,
      setProfileData,
      mergedProgress.hasProfile,
      profileVisibility,
      publicIndexingAcknowledged,
      photoVisibility,
      contactVisibility,
      companyData,
      setCompanyData,
      effectiveIntent,
      setIntent,
      selectIntentAndAdvance,
      isIntentTransitioning,
      postOnboardingRoute,
      setPostOnboardingRoute,
      nextStep,
      goToCvHeader,
      startManualCvBuild,
      enterClaimedCvFlow,
      isClaimedCvFlow,
      persistImportedCvAndGoToHeader,
      goToStepPublic,
      prevStep,
      skipStep,
      skipToComplete,
      completeOnboarding,
      finishWhereHeard,
      cvPreviewOpen,
      pendingDownloadFormat,
      cvHealthState.import,
      cvHealthState.phase,
      setCvHealthPhase,
      cvHealthState.reportId,
      cvHealthState.importScore,
      setCvHealthImportReport,
      cvHealthState.fix,
      setCvHealthFix,
      refreshCvSectionsFromServer,
      cvHealthState.pendingFix,
      setCvHealthPendingFix,
      cvHealthState.chooserDone,
      markCvHealthChooserDone,
      cvEnrichment,
      selectedCvStyleSegmentId,
      startCvEnrichment,
      ensureCvEnrichmentStarted,
      suggestedStyleSegmentId,
      cvPresetId,
      cvPresetLayout,
      applyCvPreset,
      clearCvPreset,
      isApplyingCvPreset,
      isCompleting,
      onboardingError,
      clearError,
      isLoadingState,
      isLoadingProfile,
      isLoadingCompany,
    ],
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

// ─── Hook ───────────────────────────────────────────────────────

export function useOnboarding(): OnboardingContextValue {
  const ctx = useContext(OnboardingContext);
  if (!ctx)
    throw new Error("useOnboarding must be used within OnboardingProvider");
  return ctx;
}
